import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { sanitizeProseHtml } from "@/lib/sanitize";
import { useConfirm } from "@/components/useConfirm";
import BackupButton from "@/components/BackupButton";
import LearningCompanion from "@/components/LearningCompanion";
import { useInteractiveHints } from "@/hooks/useInteractiveHints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import {
  ArrowLeft, Upload, Send, FileText, FileCode, Sparkles, Eye, MessageSquare,
  Loader2, FileAudio, FileVideo, Trash2, Copy, Bot, User as UserIcon, ExternalLink,
  Globe, Code2, Smartphone, Monitor, Tablet, Link as LinkIcon, Maximize2, Minimize2, Save, Undo2, Pencil,
  MousePointerClick, ChevronUp, ChevronDown, Wand2, Plus, Heading2, Heading3, AlignLeft, List as ListIcon,
  Image as ImageIcon, Video as VideoIcon, Minus, Type, History, RotateCcw, Clock, BoxSelect, ChevronsUp, Redo2, Palette, RefreshCw,
  Bold, Italic, Underline, AlignCenter, AlignRight, AlignJustify, Link2, ListOrdered, Hand, Heading1, Zap, Download
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SectionsPanel } from "@/components/SectionManager";
import CertStudio from "@/components/CertStudio";
import AnnounceModal from "@/components/AnnounceModal";
import { ListTree, Mail } from "lucide-react";

const PROVIDERS = [
  { id: "anthropic", model: "claude-sonnet-4-6", label: "Claude Sonnet 4.5" },
  { id: "openai", model: "gpt-5.2", label: "GPT-5.2" },
  { id: "gemini", model: "gemini-3.1-pro-preview", label: "Gemini 3 Pro" },
];

function FileBadge({ name }) {
  const lower = (name || "").toLowerCase();
  const Icon = lower.endsWith(".pdf") || lower.endsWith(".docx") || lower.endsWith(".txt")
    ? FileText
    : (lower.endsWith(".mp3") || lower.endsWith(".wav") ? FileAudio : FileVideo);
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-white border border-slate-200 rounded-md text-xs text-slate-700">
      <Icon size={13} className="text-blue-900" />
      <span className="truncate max-w-[160px]">{name}</span>
    </div>
  );
}

export default function ModuleEditor() {
  const { moduleId } = useParams();
  const [mod, setMod] = useState(null);
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [provider, setProvider] = useState(PROVIDERS[0]);
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState("preview"); // preview | source
  const [viewport, setViewport] = useState("desktop"); // desktop | tablet | mobile
  const [showClaudeHelp, setShowClaudeHelp] = useState(false);
  const [certStudioOpen, setCertStudioOpen] = useState(false);
  const [hasCertGrid, setHasCertGrid] = useState(false);
  const fileRef = useRef(null);
  const htmlArtifactRef = useRef(null);
  const jsxArtifactRef = useRef(null);
  const visualEditorRef = useRef(null);

  // Flush any pending VisualEditor changes before letting the tab actually switch.
  const switchPreviewMode = async (next) => {
    if (previewMode === "visual" && visualEditorRef.current?.isDirty?.()) {
      const ok = await visualEditorRef.current.flushSave();
      if (!ok) return; // save failed — block the switch so user can resolve
    }
    setPreviewMode(next);
  };
  const chatEndRef = useRef(null);

  const load = async () => {
    const [m, c] = await Promise.all([
      api.get(`/modules/${moduleId}`),
      api.get(`/chat/${moduleId}`),
    ]);
    setMod(m.data);
    setChat(c.data);
    const p = PROVIDERS.find((x) => x.id === m.data.ai_provider) || PROVIDERS[0];
    setProvider(p);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [moduleId]);

  // Cert Studio gate — show the entry point only when this module actually has
  // the JS-driven cert grid (currently Module 1). One HEAD-style probe; admin-only.
  useEffect(() => {
    let cancelled = false;
    api.get(`/modules/${moduleId}/cert-data`)
      .then(() => { if (!cancelled) setHasCertGrid(true); })
      .catch(() => { if (!cancelled) setHasCertGrid(false); });
    return () => { cancelled = true; };
  }, [moduleId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, streaming]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/modules/${moduleId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Extracted ${data.extracted_chars.toLocaleString()} characters from ${data.filename}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onUploadHtmlArtifact = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB. Maximum is 12 MB.`);
      if (htmlArtifactRef.current) htmlArtifactRef.current.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/modules/${moduleId}/upload-artifact`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(`HTML artifact uploaded — switched to embed mode`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      if (htmlArtifactRef.current) htmlArtifactRef.current.value = "";
    }
  };

  const onUploadJsxArtifact = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB. Maximum is 8 MB.`);
      if (jsxArtifactRef.current) jsxArtifactRef.current.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/modules/${moduleId}/upload-jsx`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(`JSX artifact uploaded — switched to React mode`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      if (jsxArtifactRef.current) jsxArtifactRef.current.value = "";
    }
  };

  const [announceOpen, setAnnounceOpen] = useState(false);
  const [announceKind, setAnnounceKind] = useState("new_module");

  const togglePublish = async () => {
    const wasPublished = !!mod?.published;
    const { data } = await api.patch(`/modules/${moduleId}`, { published: !wasPublished });
    setMod(data);
    toast.success(data.published ? "Module published" : "Unpublished");
    // Auto-prompt: when an admin transitions a module from unpublished →
    // published, open the announcement modal pre-filled as `new_module`.
    // Skipping is one click — they're never forced to send.
    if (data.published && !wasPublished) {
      setAnnounceKind("new_module");
      setAnnounceOpen(true);
    }
  };

  const changeArtifactType = async (val) => {
    const { data } = await api.patch(`/modules/${moduleId}`, { artifact_type: val });
    setMod(data);
    toast.success(`Switched to ${val.toUpperCase()}`);
  };

  const updateTitle = async (val) => {
    const { data } = await api.patch(`/modules/${moduleId}`, { title: val });
    setMod(data);
  };

  const sendMessage = async () => {
    if (!input.trim() || streaming) return;
    const msg = input.trim();
    setInput("");
    setChat((c) => [...c, { id: `tmp-u-${Date.now()}`, role: "user", content: msg, created_at: new Date().toISOString() }]);
    setChat((c) => [...c, { id: `tmp-a-${Date.now()}`, role: "assistant", content: "", created_at: new Date().toISOString(), streaming: true }]);
    setStreaming(true);

    const token = localStorage.getItem("uvc_token");
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ module_id: moduleId, message: msg, provider: provider.id, model: provider.model }),
      });
      if (!res.ok || !res.body) {
        const errBody = await res.text();
        throw new Error(errBody || "Stream failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let fullText = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(ln.slice(6));
            if (payload.type === "delta") {
              fullText += payload.content;
              // Show only the reply portion if <reply> tag exists, else partial
              const displayText = extractReplyOrPartial(fullText);
              setChat((c) => c.map((m, i) => i === c.length - 1 ? { ...m, content: displayText } : m));
            } else if (payload.type === "done") {
              setChat((c) => c.map((m, i) => i === c.length - 1 ? { ...m, content: payload.reply || m.content, streaming: false } : m));
              if (payload.artifact_updated) {
                setMod((prev) => ({ ...prev, artifact_content: payload.artifact ?? prev.artifact_content }));
                toast.success("Artifact updated");
              }
            } else if (payload.type === "error") {
              setChat((c) => c.map((m, i) => i === c.length - 1 ? { ...m, content: `⚠️ ${payload.message}`, streaming: false } : m));
              toast.error(payload.message);
            }
          } catch {/* skip malformed */}
        }
      }
    } catch (err) {
      setChat((c) => c.map((m, i) => i === c.length - 1 ? { ...m, content: `⚠️ ${err.message}`, streaming: false } : m));
      toast.error(err.message);
    } finally {
      setStreaming(false);
    }
  };

  const clearChat = async () => {
    if (!window.confirm("Clear all chat history for this module?")) return;
    await api.delete(`/chat/${moduleId}`);
    setChat([]);
    toast.success("Chat cleared");
  };

  if (!mod) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading editor...
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Announcement modal — opened either from the standalone "Notify
          learners" button or auto-prompted right after `togglePublish`
          flips the module from unpublished → published. */}
      <AnnounceModal
        open={announceOpen}
        onClose={() => setAnnounceOpen(false)}
        moduleId={moduleId}
        kind={announceKind}
      />
      {/* Top bar */}
      <header className="h-16 border-b border-slate-200 flex items-center justify-between px-5 shrink-0">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <Link to={`/admin/courses/${mod.course_id}`} className="p-2 text-slate-500 hover:text-slate-900" data-testid="back-to-course">
            <ArrowLeft size={18} />
          </Link>
          <Logo variant="compact" className="h-8 shrink-0" />
          <div className="h-6 w-px bg-slate-200 mx-1" />
          <Input
            data-testid="module-title-edit"
            value={mod.title}
            onChange={(e) => setMod({ ...mod, title: e.target.value })}
            onBlur={(e) => updateTitle(e.target.value)}
            className="bg-transparent border-0 px-0 text-base font-display font-semibold focus-visible:ring-0 max-w-md"
          />
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Select value={mod.artifact_type} onValueChange={changeArtifactType}>
            <SelectTrigger data-testid="artifact-type-toggle" className="w-[200px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="html"><span className="inline-flex items-center gap-2"><FileCode size={13}/> Rich HTML</span></SelectItem>
              <SelectItem value="markdown"><span className="inline-flex items-center gap-2"><FileText size={13}/> Markdown + Quiz</span></SelectItem>
              <SelectItem value="embed"><span className="inline-flex items-center gap-2"><Globe size={13}/> Claude HTML Artifact</span></SelectItem>
              <SelectItem value="jsx"><span className="inline-flex items-center gap-2"><Code2 size={13}/> Claude JSX/React</span></SelectItem>
              <SelectItem value="link"><span className="inline-flex items-center gap-2"><LinkIcon size={13}/> External Link / Embed URL</span></SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            data-testid="preview-as-learner"
            title="Save and open this module as a learner in a new tab"
            onClick={async () => {
              // The preview tab reads from the DB. If the admin has unsaved
              // edits, flush them first so what they see in preview matches
              // what they just changed. Without this they get the stale DB
              // copy and (rightly) think "the preview is broken".
              const dirty = visualEditorRef.current?.isDirty?.();
              if (dirty && visualEditorRef.current?.flushSave) {
                const ok = await visualEditorRef.current.flushSave();
                if (!ok) {
                  toast.error("Save failed — preview would show stale content. Fix the save error first.");
                  return;
                }
              }
              window.open(`/learn/modules/${moduleId}?preview=1`, "_blank", "noopener");
            }}
          >
            <Eye size={14} className="mr-1.5" /> Preview <ExternalLink size={11} className="ml-1.5" />
          </Button>
          {/* Top-level Save button — always visible, flushes any pending Visual-edit changes */}
          <Button
            size="sm"
            onClick={async () => {
              const dirty = visualEditorRef.current?.isDirty?.();
              if (visualEditorRef.current?.flushSave) {
                const ok = await visualEditorRef.current.flushSave();
                if (ok) toast.success(dirty ? "Module saved" : "Nothing to save", { description: dirty ? "Latest changes are live." : "No pending changes." });
              } else {
                toast.message("Switch to Visual edit to make changes", { description: "Save activates once there's something to save." });
              }
            }}
            className="bg-[#E53935] hover:bg-[#c62828] text-white gap-1.5 shadow-sm"
            data-testid="module-top-save"
            title="Save all changes in this module"
          >
            <Save size={14}/> Save module
          </Button>
          {/* Download module — flushes any unsaved visual edits, then offers the
              latest artifact as a single self-contained .html file. Embed modules
              download as .html; markdown modules download as .md; everything else
              falls back to .html with the current artifact_content. */}
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                if (visualEditorRef.current?.flushSave) {
                  const ok = await visualEditorRef.current.flushSave();
                  if (!ok) {
                    toast.error("Save failed — fix the save error before downloading.");
                    return;
                  }
                }
                const fresh = await api.get(`/modules/${moduleId}`).then((r) => r.data);
                const isMd = (fresh.artifact_type || "").toLowerCase() === "markdown";
                const ext = isMd ? "md" : "html";
                const mime = isMd ? "text/markdown" : "text/html";
                const safeTitle = (fresh.title || "module")
                  .replace(/[^\w\s-]/g, "")
                  .replace(/\s+/g, "-")
                  .toLowerCase()
                  .slice(0, 80) || "module";
                const body = fresh.artifact_content || "";
                // Wrap raw HTML fragments in a minimal document so the file opens
                // standalone in any browser. Markdown / full-doc artifacts are
                // written through unchanged.
                const looksLikeDoc = /<!doctype\s+html|<html[\s>]/i.test(body);
                const out = (isMd || looksLikeDoc) ? body : `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${(fresh.title || "Module").replace(/</g, "&lt;")}</title>
</head>
<body>
${body}
</body>
</html>`;
                const blob = new Blob([out], { type: `${mime};charset=utf-8` });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${safeTitle}.${ext}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                toast.success("Module downloaded", { description: `${safeTitle}.${ext}` });
              } catch (err) {
                toast.error("Download failed", { description: err?.message || "Try again." });
              }
            }}
            className="h-8 text-xs gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="module-top-download"
            title="Download the latest artifact (auto-saves any pending visual edits first)"
          >
            <Download size={13}/> Download module
          </Button>
          {/* Notify learners — manual trigger that opens the announcement
              modal in "update" mode (vs the auto-prompt on publish which
              opens it in "new_module" mode). Visible to admins at all
              times so you can announce material section changes inside a
              previously-published module. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setAnnounceKind("update");
              setAnnounceOpen(true);
            }}
            className="h-8 text-xs gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="module-top-notify"
            title="Email all learners that this module has been updated"
          >
            <Mail size={13}/> Notify learners
          </Button>
          <BackupButton />
          {hasCertGrid && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCertStudioOpen(true)}
              data-testid="open-cert-studio"
              className="h-8 text-xs gap-1.5 border-[#E53935]/40 text-[#E53935] hover:bg-[#E53935] hover:text-white"
              title="Open Cert Studio — move flashcards between sections, rename headers, edit fields"
            >
              <Sparkles size={13}/> Cert Studio
            </Button>
          )}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md">
            <span className="text-xs text-slate-500">Publish</span>
            <Switch data-testid="module-publish-switch" checked={mod.published} onCheckedChange={togglePublish} />
          </div>
        </div>
      </header>

      {/* Split pane */}
      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        {/* CHAT PANEL - LEFT */}
        <aside className="col-span-12 md:col-span-4 lg:col-span-4 border-r border-slate-200 bg-slate-50/60 flex flex-col h-full overflow-hidden">
          {/* Model + uploads */}
          <div className="px-4 py-3 border-b border-slate-200 bg-white shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <MessageSquare size={13} /> AI Co-author
              </div>
              <button onClick={clearChat} className="text-xs text-slate-400 hover:text-red-600" title="Clear chat" data-testid="clear-chat-button">
                <Trash2 size={13} />
              </button>
            </div>
            <Select
              value={provider.id}
              onValueChange={(id) => setProvider(PROVIDERS.find((p) => p.id === id))}
            >
              <SelectTrigger data-testid="ai-model-select" className="h-9 w-full bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`model-option-${p.id}`}>
                    <span className="inline-flex items-center gap-2"><Bot size={13}/> {p.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Uploaded files */}
          <div className="px-4 py-3 border-b border-slate-200 bg-white shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Source Material</div>
              <div className="text-[10px] text-slate-400">{(mod.source_text || "").length.toLocaleString()} chars</div>
            </div>
            <input ref={fileRef} type="file" hidden onChange={onUpload}
              accept=".pdf,.docx,.txt,.mp3,.mp4,.m4a,.wav,.webm,.mpga,.mpeg" data-testid="file-upload-input" />
            <button
              data-testid="upload-file-button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-slate-300 hover:border-blue-900 hover:bg-blue-50/30 rounded-md py-3 px-4 text-sm text-slate-600 transition-colors flex items-center justify-center gap-2"
            >
              {uploading ? <><Loader2 size={14} className="animate-spin"/> Processing...</> : <><Upload size={14}/> Upload PDF, Word, Audio or Video</>}
            </button>
            {mod.source_files?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {mod.source_files.map((f, i) => <FileBadge key={i} name={f.name} />)}
              </div>
            )}
          </div>

          {/* Claude artifact upload */}
          <div className="px-4 py-3 border-b border-slate-200 bg-white shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
                <Globe size={12}/> Claude Artifact
              </div>
              <button
                onClick={() => setShowClaudeHelp(true)}
                className="text-[10px] text-blue-900 hover:text-blue-700 font-medium"
                data-testid="claude-help-button"
              >
                How to make one?
              </button>
            </div>
            <input ref={htmlArtifactRef} type="file" hidden accept=".html,.htm" onChange={onUploadHtmlArtifact} data-testid="html-artifact-input" />
            <input ref={jsxArtifactRef} type="file" hidden accept=".jsx,.js,.tsx,.ts,.txt" onChange={onUploadJsxArtifact} data-testid="jsx-artifact-input" />
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="upload-html-artifact-button"
                onClick={() => htmlArtifactRef.current?.click()}
                disabled={uploading}
                className="border border-blue-900/30 bg-blue-50/40 hover:bg-blue-50 text-blue-900 rounded-md py-2 px-3 text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                <Code2 size={12}/> HTML
              </button>
              <button
                data-testid="upload-jsx-artifact-button"
                onClick={() => jsxArtifactRef.current?.click()}
                disabled={uploading}
                className="border border-amber-700/30 bg-amber-50 hover:bg-amber-100/70 text-amber-900 rounded-md py-2 px-3 text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                <Code2 size={12}/> JSX/React
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
              Export from Claude as <span className="font-mono">.html</span> or <span className="font-mono">.jsx</span> and drop here.
            </p>
          </div>

          {/* AI Quiz generation */}
          <QuizPanel mod={mod} onUpdate={load} />

          {/* Hero image (Gemini Nano Banana) */}
          <HeroPanel mod={mod} onUpdate={load} />

          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto thin-scroll px-4 py-4 space-y-4">
            {chat.length === 0 && (
              <div className="text-center py-8 px-4">
                <div className="mx-auto w-12 h-12 rounded-full bg-blue-50 text-blue-900 flex items-center justify-center mb-3">
                  <Sparkles size={20} />
                </div>
                <div className="text-sm font-medium text-slate-900 mb-1">Start authoring</div>
                <div className="text-xs text-slate-500 max-w-[260px] mx-auto">
                  Upload material, then ask the AI to draft, restructure, add quizzes, or rewrite sections.
                </div>
                <div className="mt-4 space-y-1.5">
                  {[
                    "Draft a lesson from the uploaded material",
                    "Add a 5-question quiz at the end",
                    "Make it more engaging for a corporate audience",
                  ].map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className="block w-full text-left text-xs text-blue-900 bg-white border border-slate-200 rounded-md px-3 py-2 hover:border-blue-900 transition-colors"
                      data-testid={`suggestion-${s.slice(0, 10)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {chat.map((m) => (
              <div key={m.id} className="space-y-1" data-testid={`chat-msg-${m.role}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                  {m.role === "user" ? <UserIcon size={11}/> : <Bot size={11}/>}
                  {m.role === "user" ? "You" : provider.label}
                </div>
                <div className={`text-sm leading-relaxed whitespace-pre-wrap break-words rounded-md p-3 ${
                  m.role === "user" ? "bg-white border border-slate-200 text-slate-800" : "bg-blue-50/40 border-l-2 border-blue-900 text-slate-800"
                } ${m.streaming ? "streaming-cursor" : ""}`}>
                  {m.content || (m.streaming ? "" : "...")}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 bg-white p-3 shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                }}
                placeholder="Ask the AI to build, refine, or quiz the artifact…"
                rows={2}
                className="flex-1 resize-none border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/40 focus:border-transparent"
              />
              <Button
                data-testid="send-chat-button"
                onClick={sendMessage}
                disabled={streaming || !input.trim()}
                className="bg-blue-900 hover:bg-blue-800 text-white h-10 w-10 p-0 shrink-0"
              >
                {streaming ? <Loader2 size={16} className="animate-spin"/> : <Send size={15}/>}
              </Button>
            </div>
          </div>
        </aside>

        {/* ARTIFACT PANEL - RIGHT */}
        <section className="col-span-12 md:col-span-8 lg:col-span-8 flex flex-col h-full overflow-hidden bg-slate-50">
          <div className="border-b border-slate-200 px-6 py-3 flex items-center justify-between shrink-0 bg-white">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-400 live-pulse" />
                <span className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Live Artifact</span>
              </div>
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-500 font-mono">
                {mod.artifact_type === "embed" ? "CLAUDE HTML"
                  : mod.artifact_type === "jsx" ? "CLAUDE JSX"
                  : mod.artifact_type === "link" ? "EXTERNAL LINK"
                  : mod.artifact_type.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* History/snapshots */}
              <SnapshotHistoryButton moduleId={mod.id} onRestored={load} />
              {(mod.artifact_type === "embed" || mod.artifact_type === "jsx") && previewMode === "preview" && (
                <div className="flex items-center gap-0.5 p-0.5 bg-slate-100 rounded-md border border-slate-200">
                  {[
                    { id: "desktop", icon: Monitor, label: "Desktop" },
                    { id: "tablet", icon: Tablet, label: "Tablet" },
                    { id: "mobile", icon: Smartphone, label: "Mobile" },
                  ].map((v) => {
                    const Icon = v.icon;
                    return (
                      <button
                        key={v.id}
                        onClick={() => setViewport(v.id)}
                        title={v.label}
                        data-testid={`viewport-${v.id}`}
                        className={`p-1.5 rounded transition-colors ${viewport === v.id ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
                      >
                        <Icon size={13} />
                      </button>
                    );
                  })}
                </div>
              )}
              <Tabs value={previewMode} onValueChange={switchPreviewMode}>
                <TabsList className="h-8">
                  <TabsTrigger value="preview" className="text-xs px-3" data-testid="tab-preview">Preview</TabsTrigger>
                  {mod.artifact_type !== "link" && (
                    <TabsTrigger value="visual" className="text-xs px-3" data-testid="tab-visual">
                      <MousePointerClick size={11} className="mr-1" /> Visual edit
                    </TabsTrigger>
                  )}
                  {mod.artifact_type !== "link" && (
                    <TabsTrigger value="source" className="text-xs px-3" data-testid="tab-source">
                      <Pencil size={11} className="mr-1" /> Edit code
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </div>
          </div>

          <div className="flex-1 overflow-hidden bg-slate-50">
            {!mod.artifact_content && mod.artifact_type !== "link" && previewMode === "preview" ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-md px-6">
                  <div className="mx-auto w-16 h-16 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-4">
                    <Sparkles size={26} className="text-slate-300" strokeWidth={1.3} />
                  </div>
                  <h3 className="text-lg font-display font-semibold text-slate-900 mb-2">The artifact appears here</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Chat with the AI to draft a lesson, upload a Claude HTML artifact, or — easiest — switch to <strong>Visual edit</strong> and start writing.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewMode("visual")}
                    className="h-8 text-xs"
                    data-testid="empty-go-visual-button"
                  >
                    <MousePointerClick size={12} className="mr-1.5"/> Open Visual editor
                  </Button>
                </div>
              </div>
            ) : previewMode === "preview" ? (
              mod.artifact_type === "link" ? (
                <LinkArtifactPanel mod={mod} onSaved={load} />
              ) : (mod.artifact_type === "embed" || mod.artifact_type === "jsx") ? (
                <ArtifactIframe
                  html={mod.artifact_content}
                  mode={mod.artifact_type}
                  viewport={viewport}
                  moduleId={moduleId}
                  learner={{ name: "Admin Preview", email: "admin@univicoustic.com" }}
                />
              ) : (
                <PreviewWithAria mod={mod} />
              )
            ) : previewMode === "visual" ? (
              <VisualEditor
                ref={visualEditorRef}
                key={mod.id + "-vis"}
                content={mod.artifact_content || ""}
                artifactType={mod.artifact_type}
                onSave={async (next, newType) => {
                  const patch = { artifact_content: next };
                  if (newType && newType !== mod.artifact_type) patch.artifact_type = newType;
                  await api.patch(`/modules/${mod.id}`, patch);
                  await load();
                }}
              />
            ) : (
              <SourceEditor
                key={mod.id + "-src"}
                content={mod.artifact_content || ""}
                artifactType={mod.artifact_type}
                onSave={async (next) => {
                  await api.patch(`/modules/${mod.id}`, { artifact_content: next });
                  await load();
                }}
              />
            )}
          </div>
        </section>
      </div>

      <ClaudePromptDialog open={showClaudeHelp} onOpenChange={setShowClaudeHelp} moduleTitle={mod.title} />
      {certStudioOpen && (
        <CertStudio
          moduleId={mod.id}
          onClose={() => setCertStudioOpen(false)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

function extractReplyOrPartial(text) {
  if (text.includes("<reply>")) {
    const idx = text.indexOf("<reply>") + 7;
    const end = text.indexOf("</reply>");
    return end > -1 ? text.slice(idx, end).trim() : text.slice(idx).trim();
  }
  // Hide <artifact> body from chat display
  if (text.includes("<artifact>")) {
    return text.split("<artifact>")[0].trim() || "Generating artifact…";
  }
  return text;
}

// Minimal markdown renderer (headings, bold, italic, lists, code, links)
export function MarkdownRender({ source }) {
  const html = mdToHtml(source || "");
  return <div className="artifact-prose" dangerouslySetInnerHTML={{ __html: sanitizeProseHtml(html) }} />;
}

// Static preview used in the editor's "Preview" tab — same simple render the
// existing preview used to do, but with the Aria interactive-hint observer
// wired up so admins can verify [data-uvc-interactive] markers without
// leaving the editor.
function PreviewWithAria({ mod }) {
  const proseRef = useRef(null);
  const ariaRef = useRef(null);
  useInteractiveHints({ proseRef, ariaRef, enabled: true, deps: [mod.id, mod.artifact_content] });
  return (
    <div className="h-full overflow-y-auto thin-scroll bg-white relative">
      <div className="max-w-3xl mx-auto px-10 py-10">
        {mod.artifact_type === "html" ? (
          <div ref={proseRef} className="artifact-prose" dangerouslySetInnerHTML={{ __html: sanitizeProseHtml(mod.artifact_content) }} />
        ) : (
          <div ref={proseRef}>
            <MarkdownRender source={mod.artifact_content} />
          </div>
        )}
      </div>
      <LearningCompanion ref={ariaRef} />
    </div>
  );
}

function escHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function mdToHtml(md) {
  // code blocks
  let out = md.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => `<pre><code class="language-${lang}">${escHtml(code)}</code></pre>`);
  // headings
  out = out.replace(/^###### (.*)$/gm, "<h6>$1</h6>")
           .replace(/^##### (.*)$/gm, "<h5>$1</h5>")
           .replace(/^#### (.*)$/gm, "<h4>$1</h4>")
           .replace(/^### (.*)$/gm, "<h3>$1</h3>")
           .replace(/^## (.*)$/gm, "<h2>$1</h2>")
           .replace(/^# (.*)$/gm, "<h1>$1</h1>");
  // bold/italic
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
           .replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  // inline code
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  // links
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  // blockquotes
  out = out.replace(/^> (.*)$/gm, "<blockquote>$1</blockquote>");
  // lists
  out = out.replace(/(?:^|\n)((?:- .+\n?)+)/g, (m, block) => {
    const items = block.trim().split("\n").map((l) => `<li>${l.replace(/^- /, "")}</li>`).join("");
    return `\n<ul>${items}</ul>`;
  });
  out = out.replace(/(?:^|\n)((?:\d+\. .+\n?)+)/g, (m, block) => {
    const items = block.trim().split("\n").map((l) => `<li>${l.replace(/^\d+\. /, "")}</li>`).join("");
    return `\n<ol>${items}</ol>`;
  });
  // paragraphs (split by blank line, skip those already wrapped)
  out = out.split(/\n{2,}/).map((para) => {
    const trimmed = para.trim();
    if (!trimmed) return "";
    if (/^<(h\d|ul|ol|pre|blockquote|table)/.test(trimmed)) return trimmed;
    return `<p>${trimmed.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return out;
}


// =====================================================================
// Responsive iframe renderer for Claude HTML / JSX artifacts.
// Injects helpers: window.uvcSubmitScore({score, max, details})
// and window.uvcComplete({...meta}) — these post back to parent.
// =====================================================================
const SCORE_HELPER = `
  <script>
    window.uvcSubmitScore = function(data){ try { window.parent.postMessage(Object.assign({type:'uvc-score'}, data), '*'); } catch(e){} };
    window.uvcComplete = function(meta){ try { window.parent.postMessage({type:'uvc-complete', meta: meta||{}}, '*'); } catch(e){} };
    (function(){
      function reportScroll(){
        try {
          var doc = document.documentElement;
          var max = Math.max(doc.scrollHeight - window.innerHeight, 1);
          var pct = Math.min(100, Math.max(0, (window.scrollY / max) * 100));
          window.parent.postMessage({type:'uvc-scroll', pct: pct}, '*');
        } catch(e){}
      }
      window.addEventListener('scroll', reportScroll, {passive: true});
      window.addEventListener('load', reportScroll);
      setTimeout(reportScroll, 600);
      setTimeout(reportScroll, 2000);
    })();

    /* ---- Section ticker bridge -----------------------------------------
     * Detects all in-iframe h2/h3 elements + opt-in [data-uvc-section]
     * blocks, gives each one a stable id, posts the section list once on
     * load, then posts a {type:'uvc-section-seen', id} every time a section
     * enters the lower-third of the viewport (i.e. the learner has reached
     * that section while reading).
     * ------------------------------------------------------------------- */
    (function(){
      function slug(s){
        return String(s||'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'').slice(0,80) || ('section-'+Math.random().toString(36).slice(2,8));
      }
      function detect(){
        var nodes = document.querySelectorAll('h2, h3, [data-uvc-section]');
        var seen = {}; var idx = 0;
        var sections = [];
        nodes.forEach(function(el){
          // Skip clearly-decorative / hidden headings
          if (!el.offsetParent && el.tagName !== 'H1') return;
          var text = (el.getAttribute('data-uvc-section') || el.textContent || '').trim();
          if (!text) return;
          if (text.length < 3 || text.length > 200) return;
          var base = slug(text); var id = base; var n = 1;
          while (seen[id]) { id = base + '-' + (++n); }
          seen[id] = 1;
          el.setAttribute('data-uvc-section-id', id);
          sections.push({ id: id, label: text, order: idx++ });
        });
        return sections;
      }

      var SECTIONS = [];
      var SEEN = Object.create(null);

      function postSeen(id, label){
        if (SEEN[id]) return;
        SEEN[id] = true;
        try { window.parent.postMessage({type:'uvc-section-seen', id: id, label: label}, '*'); } catch(e){}
      }

      function setupObserver(){
        SECTIONS = detect();
        if (!SECTIONS.length) return;
        try {
          window.parent.postMessage({type:'uvc-sections-detected', sections: SECTIONS}, '*');
        } catch(e){}
        var io;
        try {
          io = new IntersectionObserver(function(entries){
            entries.forEach(function(e){
              if (e.isIntersecting) {
                var id = e.target.getAttribute('data-uvc-section-id');
                var label = (e.target.getAttribute('data-uvc-section') || e.target.textContent || '').trim();
                if (id) postSeen(id, label);
              }
            });
          }, { rootMargin: '0px 0px -55% 0px', threshold: 0.01 });
          document.querySelectorAll('[data-uvc-section-id]').forEach(function(el){ io.observe(el); });
        } catch(e){ /* old browser fallback below */ }
      }
      // Run after layout settles; re-run a couple times for late-mount JSX.
      setTimeout(setupObserver, 400);
      setTimeout(function(){
        var fresh = detect();
        // If we found more sections after late mount, re-broadcast
        if (fresh.length > SECTIONS.length) {
          SECTIONS = fresh;
          try { window.parent.postMessage({type:'uvc-sections-detected', sections: SECTIONS}, '*'); } catch(e){}
          setupObserver();
        }
      }, 2000);
    })();

    /* ---- Aria interactive-marker bridge -----------------------------------
     * Watches in-iframe [data-uvc-interactive] blocks and forwards
     * postMessages to the parent so it can pop Aria at the right screen spot.
     * Re-fires every time the block re-enters the viewport (after leaving it
     * first) so users who scroll back up still get the alert. mousedown /
     * touchstart on the element silences it until next viewport re-entry.
     * --------------------------------------------------------------------- */
    (function(){
      var SETUP_DELAY = 600;
      var FIRE_DELAY = 250;
      var seq = 0;
      var idMap = new WeakMap ? new WeakMap() : null;

      // Per-element state: { ready: bool, interacted: bool }
      var stateMap = new WeakMap ? new WeakMap() : null;
      var pending = new Map ? new Map() : null;

      function stateOf(el){
        if (!stateMap) return { ready:true, interacted:false };
        var s = stateMap.get(el);
        if (!s) { s = { ready:true, interacted:false }; stateMap.set(el, s); }
        return s;
      }
      function keyFor(el){
        if (!idMap) return ++seq;
        if (!idMap.has(el)) idMap.set(el, ++seq);
        return idMap.get(el);
      }
      function rectOf(el){
        var r = el.getBoundingClientRect();
        return { left:r.left, top:r.top, width:r.width, height:r.height };
      }
      function postShow(el){
        var s = stateOf(el);
        if (!s.ready || s.interacted) return;
        s.ready = false; // arms only once per viewport entry; resets on leave
        var msg = (el.getAttribute('data-uvc-interactive-msg') || '').trim()
          || 'Interactive Feature Alert! Read carefully and tap along.';
        try { el.setAttribute('data-uvc-interactive-hint','1'); } catch(e){}
        try { el.scrollIntoView({behavior:'smooth', block:'center'}); } catch(e){}
        try {
          window.parent.postMessage({
            type:'uvc-interactive-show',
            id: keyFor(el),
            rect: rectOf(el),
            msg: msg,
          }, '*');
        } catch(e){}
        setTimeout(function(){
          try { el.removeAttribute('data-uvc-interactive-hint'); } catch(e){}
        }, 4500);
      }
      function postHide(el){
        try {
          window.parent.postMessage({ type:'uvc-interactive-hide', id: keyFor(el) }, '*');
        } catch(e){}
      }
      function armEl(el){
        var s = stateOf(el);
        if (!s.ready || s.interacted || (pending && pending.has(el))) return;
        var t = setTimeout(function(){
          if (pending) pending.delete(el);
          postShow(el);
        }, FIRE_DELAY);
        if (pending) pending.set(el, t);
      }
      function cancelEl(el){
        if (!pending) return;
        var t = pending.get(el);
        if (t) { clearTimeout(t); pending.delete(el); }
      }

      function setup(){
        var nodes = document.querySelectorAll('[data-uvc-interactive]');
        if (!nodes.length) return;
        nodes.forEach(function(n){
          var onInteract = function(){
            // User engaged with the element — silence it for this session.
            stateOf(n).interacted = true;
            cancelEl(n);
            try { n.removeAttribute('data-uvc-interactive-hint'); } catch(e){}
            postHide(n);
          };
          n.addEventListener('mousedown', onInteract, {passive:true});
          n.addEventListener('touchstart', onInteract, {passive:true});
          n.addEventListener('focusin', onInteract, {passive:true});
        });
        var io = new IntersectionObserver(function(entries){
          for (var i=0;i<entries.length;i++){
            var e = entries[i];
            if (e.isIntersecting && e.intersectionRatio > 0.4) {
              armEl(e.target);
            } else {
              cancelEl(e.target);
              // The block left the viewport — re-arm so re-entry triggers Aria again.
              var s = stateOf(e.target);
              if (!s.interacted) s.ready = true;
              postHide(e.target);
            }
          }
        }, { threshold:[0,0.4,0.8] });
        nodes.forEach(function(n){ io.observe(n); });

        /* Stream rect updates while a hint is being shown so the parent
         * can keep Aria glued to the moving element during scroll. */
        var rafToken = null;
        function streamRects(){
          rafToken = null;
          nodes.forEach(function(n){
            // Only stream while currently being shown (ready==false && !interacted)
            var s = stateOf(n);
            if (!s.ready && !s.interacted) {
              try { window.parent.postMessage({type:'uvc-interactive-move', id:keyFor(n), rect:rectOf(n)}, '*'); } catch(e){}
            }
          });
        }
        function schedule(){ if (rafToken==null) rafToken = requestAnimationFrame(streamRects); }
        window.addEventListener('scroll', schedule, {passive:true});
        window.addEventListener('resize', schedule, {passive:true});
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function(){ setTimeout(setup, SETUP_DELAY); });
      } else {
        setTimeout(setup, SETUP_DELAY);
      }
    })();
  </script>
`;

// Safety net for artifacts that build a slideshow/lesson list but forget to
// activate the first section on init. Without this, the `.lesson` elements
// stay `display:none` until the learner manually clicks a nav button.
const SLIDESHOW_SAFETY_NET = `
  <script>
    (function(){
      function activateFirstLesson(){
        try {
          var lessons = document.querySelectorAll('.lesson');
          if (!lessons.length) return;
          var hasActive = false;
          lessons.forEach(function(l){ if (l.classList && l.classList.contains('is-active')) hasActive = true; });
          if (!hasActive) {
            lessons[0].classList.add('is-active');
            var firstBtn = document.querySelector('.lesson-btn');
            if (firstBtn) firstBtn.classList.add('active');
          }
        } catch (e) { /* ignore */ }
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function(){ setTimeout(activateFirstLesson, 80); });
      } else {
        setTimeout(activateFirstLesson, 80);
      }
    })();
  </script>
`;

// Rewrite any absolute backend URLs baked into the artifact so they always
// resolve against the CURRENT environment's backend. Without this, an image
// uploaded on preview (whose absolute URL got saved into the artifact HTML)
// breaks when the same module is viewed on production (or vice-versa).
// Also absolutizes bare `/api/media|static/...` paths — necessary because
// `srcDoc`-loaded iframes have no useful base URL to resolve relative paths.
function rewriteBackendUrls(html) {
  if (!html) return html;
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (!backend) return html;
  let out = html;
  // 1) Cross-env absolute URLs → current env
  out = out.replace(
    /https?:\/\/[a-zA-Z0-9-]+(?:\.preview\.emergentagent\.com|\.emergent\.host)(\/api\/(?:media|static)\/[A-Za-z0-9._-]+)/g,
    backend + "$1",
  );
  // 2) Relative /api/media|static/... in src/href attrs → absolute current env
  //    (only inside attribute values to avoid touching JS string literals).
  out = out.replace(
    /(\b(?:src|href)\s*=\s*["'])(\/api\/(?:media|static)\/[A-Za-z0-9._-]+)/g,
    `$1${backend}$2`,
  );
  return out;
}

function buildHtmlSrcDoc(src) {
  let out = rewriteBackendUrls(src);
  if (!/<meta\s+name=["']viewport/i.test(out)) {
    if (/<head[^>]*>/i.test(out)) {
      out = out.replace(/<head([^>]*)>/i, '<head$1><meta name="viewport" content="width=device-width, initial-scale=1">');
    } else {
      out = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body>${out}</body></html>`;
    }
  }
  // Scrub any baked-in children inside the slideshow's #railList / #mobileSel
  // containers. The artifact's inline script always appends a fresh set on load;
  // if a previous save accidentally persisted the runtime-built children, we'd
  // render them PLUS the freshly-built ones (the "repeating 01..10 rail" bug).
  // Force these containers empty so the runtime build is the sole source.
  out = out.replace(
    /(<nav\b[^>]*id=["']railList["'][^>]*>)[\s\S]*?(<\/nav>)/i,
    "$1$2",
  );
  out = out.replace(
    /(<select\b[^>]*id=["']mobileSel["'][^>]*>)[\s\S]*?(<\/select>)/i,
    "$1$2",
  );
  // Inject the score helper + slideshow safety net just before </body> (or </html>).
  // SLIDESHOW_SAFETY_NET goes LAST so it runs after the artifact's own init.
  const tail = `${SCORE_HELPER}${SLIDESHOW_SAFETY_NET}`;
  if (/<\/body>/i.test(out)) out = out.replace(/<\/body>/i, `${tail}</body>`);
  else if (/<\/html>/i.test(out)) out = out.replace(/<\/html>/i, `${tail}</html>`);
  else out = out + tail;
  return out;
}

function buildJsxSrcDoc(jsxSrc) {
  // Strip ES module syntax that Babel-standalone can't fully handle as a script
  let code = rewriteBackendUrls(jsxSrc)
    .replace(/^\s*import\s+.*?;?\s*$/gm, "")             // remove imports
    .replace(/export\s+default\s+function\s+(\w+)/g, "function App")
    .replace(/export\s+default\s+class\s+(\w+)/g, "class App")
    .replace(/export\s+default\s+(\w+)\s*;?/g, "var App = $1;")
    .replace(/export\s+default\s+/g, "var App = ")
    .replace(/^\s*export\s+(const|let|var|function|class)\s+/gm, "$1 ");

  // Escape closing script tags inside user code (defensive)
  code = code.replace(/<\/script>/gi, "<\\/script>");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<script src="https://cdn.tailwindcss.com"></script>
<script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
<script src="https://unpkg.com/@babel/standalone@7/babel.min.js"></script>
<style>
  html,body{margin:0;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,sans-serif;background:#fff;color:#0f172a;}
  #uvc-error{padding:24px;color:#b91c1c;font-family:ui-monospace,Menlo,monospace;font-size:13px;white-space:pre-wrap;background:#fef2f2;border-bottom:1px solid #fecaca;}
</style>
</head>
<body>
<div id="root"></div>
<div id="uvc-error" style="display:none"></div>
${SCORE_HELPER}
<script type="text/babel" data-presets="react" data-type="module">
const { useState, useEffect, useRef, useCallback, useMemo, useReducer, useContext, Fragment, createContext } = React;
try {
${code}

  let __Component = (typeof App !== 'undefined') ? App
    : (typeof Module !== 'undefined') ? Module
    : (typeof Main !== 'undefined') ? Main
    : (typeof Component !== 'undefined') ? Component
    : null;
  if (__Component) {
    ReactDOM.createRoot(document.getElementById('root')).render(<__Component />);
  } else {
    document.getElementById('uvc-error').style.display='block';
    document.getElementById('uvc-error').textContent='No React component found. Please define function App() as the root component.';
  }
} catch (err) {
  document.getElementById('uvc-error').style.display='block';
  document.getElementById('uvc-error').textContent = 'Render error: ' + (err && err.message ? err.message : err);
  console.error(err);
}
</script>
</body>
</html>`;
}

export function ArtifactIframe({ html, mode = "embed", viewport = "desktop", moduleId, learner }) {
  const widths = { desktop: "100%", tablet: "820px", mobile: "390px" };
  const srcDoc = React.useMemo(
    () => (mode === "jsx" ? buildJsxSrcDoc(html || "") : buildHtmlSrcDoc(html || "")),
    [html, mode]
  );

  React.useEffect(() => {
    if (!moduleId || !learner?.name) return undefined;
    const handler = async (e) => {
      const d = e?.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "uvc-score") {
        try {
          await api.post(`/progress`, {
            module_id: moduleId,
            learner_id: learner.id || null,
            learner_name: learner.name,
            learner_email: learner.email || null,
            score: d.score ?? null,
            max_score: d.max ?? d.max_score ?? null,
            completed: true,
            details: d.details || {},
          });
          toast.success(`Score recorded for ${learner.name}`);
        } catch (err) {
          toast.error("Failed to record score");
        }
      } else if (d.type === "uvc-complete") {
        try {
          await api.post(`/progress`, {
            module_id: moduleId,
            learner_id: learner.id || null,
            learner_name: learner.name,
            learner_email: learner.email || null,
            completed: true,
            details: d.meta || {},
          });
          toast.success(`Completion recorded for ${learner.name}`);
        } catch {/* ignore */}
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [moduleId, learner]);

  return (
    <div className="h-full w-full flex items-stretch justify-center p-3 bg-slate-100 overflow-auto thin-scroll">
      <div
        className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden transition-all duration-300 mx-auto"
        style={{ width: widths[viewport], maxWidth: "100%", height: "100%", minHeight: "500px" }}
        data-testid={`artifact-iframe-wrapper-${viewport}`}
      >
        <iframe
          title="artifact-preview"
          srcDoc={srcDoc}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          className="w-full h-full border-0 block"
          data-testid="artifact-iframe"
        />
      </div>
    </div>
  );
}

// =====================================================================
// Claude prompt help dialog
// =====================================================================
const CLAUDE_PROMPT_TEMPLATE = `Create an interactive learning module as a complete, self-contained artifact for "{TITLE}".

Two acceptable output formats — choose whichever Claude prefers:

OPTION A — Pure HTML artifact (.html)
- SINGLE self-contained .html file (DOCTYPE, <html>, <head>, <body>)
- <meta name="viewport" content="width=device-width, initial-scale=1"> for mobile
- Style with inline <style> or <script src="https://cdn.tailwindcss.com"></script>
- All JavaScript inline within <script> tags
- Fully responsive (desktop 1200px+, tablet 768px, mobile 390px)

OPTION B — React/JSX artifact (.jsx)
- Define ONE root component as: function App() { ... }
- No import/export statements at the top level (the LMS auto-strips them, but cleaner without)
- You may use hooks: useState, useEffect, useRef, useCallback, useMemo, useReducer, useContext
- Use Tailwind utility classes (the LMS injects Tailwind CDN automatically)
- No external libraries other than React (already provided by the LMS)

CONTENT (both formats):
- Engaging hero / intro section
- Core learning content broken into digestible sections
- At least one interactive element (knowledge check, flip cards, tabs, scenario simulator)
- A 5-question quiz at the end with instant feedback and a "Show Score" button

SCORE REPORTING (IMPORTANT for LMS analytics):
When the learner submits the quiz, call this global function to record the score:
  window.uvcSubmitScore({ score: <correct_count>, max: <total_questions>, details: { answers: [...] } });
Also call window.uvcComplete({ time_seconds: <duration> }) when the lesson is fully completed.
These are safe no-ops in standalone preview.

COLOR PALETTE: navy (#1E3A8A), slate, amber (#F59E0B) accents — matches UniVicoustic brand.

Once generated, click the artifact's "⋯" menu → Copy/Download → upload the .html or .jsx file into the UniVicoustic LMS.`;

export function ClaudePromptDialog({ open, onOpenChange, moduleTitle }) {
  const filled = CLAUDE_PROMPT_TEMPLATE.replace("{TITLE}", moduleTitle || "this module");
  const copy = () => { navigator.clipboard.writeText(filled); toast.success("Prompt copied — paste into Claude"); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Globe size={18} className="text-blue-900"/> How to make a Claude artifact for this module
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-700">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Open <a href="https://claude.ai" target="_blank" rel="noopener noreferrer" className="text-blue-900 underline">claude.ai</a> (Claude Sonnet 4.5 or Opus).</li>
            <li>Paste the prompt below (it already targets the artifact format the LMS expects).</li>
            <li>Claude will produce an HTML artifact in the right pane. Iterate with follow-up messages until you&apos;re happy.</li>
            <li>Click the artifact&apos;s <span className="font-mono">⋯</span> menu → <strong>Copy</strong>, or download as <span className="font-mono">.html</span>.</li>
            <li>Come back here and click <strong>Upload HTML artifact</strong> — the LMS will switch this module to embed mode and render it inside a responsive sandboxed iframe.</li>
          </ol>
          <div className="relative">
            <pre className="text-xs bg-slate-900 text-slate-100 rounded-md p-4 max-h-72 overflow-auto thin-scroll whitespace-pre-wrap font-mono">
{filled}
            </pre>
            <button onClick={copy} data-testid="copy-prompt-button" className="absolute top-2 right-2 px-2 py-1 text-[11px] bg-white/10 hover:bg-white/20 text-white rounded inline-flex items-center gap-1">
              <Copy size={11}/> Copy
            </button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Got it</Button>
          <Button onClick={copy} className="bg-blue-900 hover:bg-blue-800 text-white">
            <Copy size={14} className="mr-1.5"/> Copy prompt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// Quiz generation panel (sidebar)
// =====================================================================
function QuizPanel({ mod, onUpdate }) {
  const [busy, setBusy] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const [threshold, setThreshold] = React.useState(mod.pass_threshold ?? 70);
  const [difficulty, setDifficulty] = React.useState(mod.quiz_difficulty ?? "medium");
  const [autoQuiz, setAutoQuiz] = React.useState(!!mod.auto_quiz);
  const count = mod.quiz?.length || 0;
  const genStatus = mod.quiz_generation_status || "idle";

  // Sync settings when module reloads
  React.useEffect(() => {
    setThreshold(mod.pass_threshold ?? 70);
    setDifficulty(mod.quiz_difficulty ?? "medium");
    setAutoQuiz(!!mod.auto_quiz);
  }, [mod.pass_threshold, mod.quiz_difficulty, mod.auto_quiz]);

  // Poll while background generation runs
  React.useEffect(() => {
    if (genStatus !== "generating") return undefined;
    const t = setInterval(() => { onUpdate(); }, 4000);
    return () => clearInterval(t);
  }, [genStatus, onUpdate]);

  const saveSettings = async (next) => {
    try {
      await api.patch(`/modules/${mod.id}`, next);
      await onUpdate();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/modules/${mod.id}/generate-quiz`, null, { params: { difficulty } });
      toast.success(`Generated ${data.count} ${difficulty} questions`);
      await onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Delete the generated quiz?")) return;
    try {
      await api.delete(`/modules/${mod.id}/quiz`);
      toast.success("Quiz cleared");
      await onUpdate();
    } catch (e) {
      toast.error("Failed to clear");
    }
  };

  const DIFF_STYLES = {
    easy: "border-emerald-300 bg-emerald-50 text-emerald-800",
    medium: "border-amber-300 bg-amber-50 text-amber-800",
    hard: "border-red-300 bg-red-50 text-red-800",
  };

  return (
    <div className="px-4 py-3 border-b border-slate-200 bg-white shrink-0">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
          <Sparkles size={12}/> AI Quiz
        </div>
        {genStatus === "generating" ? (
          <span className="text-[10px] text-blue-900 bg-blue-50 px-2 py-0.5 rounded inline-flex items-center gap-1 animate-pulse" data-testid="quiz-gen-status-generating">
            <Loader2 size={9} className="animate-spin"/> Auto-generating…
          </span>
        ) : genStatus === "failed" ? (
          <span className="text-[10px] text-red-700 bg-red-50 px-2 py-0.5 rounded" title={mod.quiz_generation_error} data-testid="quiz-gen-status-failed">
            Auto-gen failed
          </span>
        ) : count > 0 ? (
          <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
            {count} questions · {mod.quiz_difficulty || "medium"}
          </span>
        ) : null}
      </div>

      {/* Difficulty selector */}
      <div className="flex items-center gap-1 mb-2" data-testid="difficulty-selector">
        {["easy", "medium", "hard"].map((d) => (
          <button
            key={d}
            onClick={() => { setDifficulty(d); saveSettings({ quiz_difficulty: d }); }}
            data-testid={`difficulty-${d}`}
            className={`flex-1 text-[10px] uppercase tracking-wider font-semibold py-1.5 rounded border transition-colors ${
              difficulty === d
                ? DIFF_STYLES[d]
                : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      <button
        data-testid="generate-quiz-button"
        onClick={generate}
        disabled={busy}
        className="w-full border border-emerald-700/30 bg-emerald-50 hover:bg-emerald-100/70 text-emerald-800 rounded-md py-2 px-3 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
      >
        {busy ? <><Loader2 size={12} className="animate-spin"/> Generating…</> : count > 0 ? <><Sparkles size={12}/> Regenerate Quiz</> : <><Sparkles size={12}/> Generate Quiz from Content</>}
      </button>

      {/* Auto-generate toggle */}
      <label className="flex items-start gap-2 mt-3 cursor-pointer group" data-testid="auto-quiz-toggle-label">
        <input
          type="checkbox"
          checked={autoQuiz}
          onChange={(e) => { setAutoQuiz(e.target.checked); saveSettings({ auto_quiz: e.target.checked }); }}
          data-testid="auto-quiz-toggle"
          className="mt-0.5 accent-[#E53935]"
        />
        <div className="flex-1">
          <div className="text-[11px] text-slate-700 font-medium">Auto-generate after every upload</div>
          <div className="text-[10px] text-slate-500 leading-tight">
            Whenever new source material or an artifact is uploaded, regenerate the quiz at <strong>{difficulty}</strong> difficulty.
          </div>
        </div>
      </label>

      {count > 0 && (
        <>
          {/* Quiz settings: threshold + max attempts */}
          <div className="mt-3 space-y-2.5 pt-3 border-t border-slate-100">
            <div>
              <div className="flex items-center justify-between text-[10px] text-slate-600 mb-1">
                <span className="uppercase tracking-wider font-semibold">Pass Threshold</span>
                <span className="font-mono text-[#E53935] font-bold">{threshold}%</span>
              </div>
              <input
                type="range" min="0" max="100" step="5"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                onMouseUp={() => saveSettings({ pass_threshold: threshold })}
                onTouchEnd={() => saveSettings({ pass_threshold: threshold })}
                data-testid="pass-threshold-slider"
                className="w-full accent-[#E53935]"
              />
            </div>
            <div>
              <div className="text-[10px] text-slate-600 mb-1 uppercase tracking-wider font-semibold">
                Attempts
              </div>
              <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded px-2 py-1" data-testid="attempts-policy-note">
                Unlimited (platform policy)
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowPreview(!showPreview)} className="flex-1 text-[10px] text-slate-600 hover:text-[#E53935]" data-testid="toggle-quiz-preview">
              {showPreview ? "Hide" : "Preview"} questions
            </button>
            <button onClick={clear} className="text-[10px] text-slate-400 hover:text-red-600" data-testid="clear-quiz-button">
              Clear
            </button>
          </div>
        </>
      )}
      {showPreview && count > 0 && (
        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto thin-scroll" data-testid="quiz-preview-list">
          {mod.quiz.map((q, i) => (
            <div key={i} className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2">
              <div className="font-medium text-slate-800 mb-1">{i + 1}. {q.q}</div>
              {q.options.map((opt, oi) => (
                <div key={oi} className={`text-slate-600 ${oi === q.correct_idx ? "text-emerald-700 font-medium" : ""}`}>
                  {oi === q.correct_idx ? "✓" : "·"} {opt}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// =====================================================================
// Hero image panel — generate a Gemini Nano Banana illustration matched
// to the module's title. Shown above prose modules in the learner view.
// =====================================================================
function HeroPanel({ mod, onUpdate }) {
  const [busy, setBusy] = React.useState(false);
  const hasHero = !!mod.hero_image;

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/modules/${mod.id}/generate-hero`);
      toast.success("Hero illustration generated");
      await onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hero generation failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the hero illustration?")) return;
    try {
      await api.patch(`/modules/${mod.id}`, { hero_image: null });
      toast.success("Hero image removed");
      await onUpdate();
    } catch (e) {
      toast.error("Failed to remove hero image");
    }
  };

  return (
    <div className="px-4 py-3 border-b border-slate-200 bg-white shrink-0" data-testid="hero-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
          <ImageIcon size={12}/> Hero Illustration
        </div>
        {hasHero && (
          <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
            generated
          </span>
        )}
      </div>

      {hasHero && (
        <div className="mb-2 rounded-md overflow-hidden border border-slate-200 bg-slate-100 aspect-[16/7]">
          <img
            src={mod.hero_image}
            alt=""
            data-testid="hero-thumb"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <button
        data-testid="generate-hero-button"
        onClick={generate}
        disabled={busy}
        className="w-full border border-[#E53935]/30 bg-[#E53935]/5 hover:bg-[#E53935]/10 text-[#E53935] rounded-md py-2 px-3 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
      >
        {busy
          ? <><Loader2 size={12} className="animate-spin"/> Generating with Nano Banana…</>
          : hasHero
            ? <><RefreshCw size={12}/> Regenerate Hero</>
            : <><Sparkles size={12}/> Generate Hero from Title</>}
      </button>

      {hasHero && (
        <button
          onClick={remove}
          data-testid="remove-hero-button"
          className="w-full mt-1.5 text-[10px] text-slate-400 hover:text-red-600"
        >
          Remove hero image
        </button>
      )}

      <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
        Cinematic header illustration generated from the module title and content brief. Shown above prose modules in the learner reading view.
      </p>
    </div>
  );
}


// =====================================================================
// External Link / Embed URL artifact — admin editor + reusable viewer
// =====================================================================

/** Common rules for which URLs we'll render inside an iframe. Anything that doesn't pass
 *  is shown as a "click to open in new tab" card instead (many sites set X-Frame-Options
 *  which silently breaks the iframe, but admins are warned). */
function normalizeArtifactUrl(raw) {
  const u = (raw || "").trim();
  if (!u) return "";
  if (/^(https?:)?\/\//i.test(u)) return u.startsWith("//") ? "https:" + u : u;
  return "https://" + u;
}

function toYouTubeEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.replace("/", "")}`;
    }
    if (/(^|\.)youtube\.com$/.test(u.hostname)) {
      if (u.pathname === "/watch") return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
      if (u.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}`;
    }
  } catch (e) {/* ignore */}
  return null;
}

function toVimeoEmbed(url) {
  try {
    const u = new URL(url);
    if (/(^|\.)vimeo\.com$/.test(u.hostname)) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
  } catch (e) {/* ignore */}
  return null;
}

/** Reusable viewer for the "link" artifact type. Used by both the admin live preview
 *  and the learner reading experience. */
export function LinkArtifactView({ url, embedded = false }) {
  const clean = normalizeArtifactUrl(url);
  const yt = toYouTubeEmbed(clean);
  const vimeo = toVimeoEmbed(clean);
  const embedSrc = yt || vimeo || clean;
  const isVideoEmbed = !!(yt || vimeo);
  const isClaudeShare = /(^|\.)claude\.(ai|com)$/i.test((() => { try { return new URL(clean).hostname; } catch { return ""; } })());

  if (!clean) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 text-sm">
        Paste a URL to embed it here.
      </div>
    );
  }

  return (
    <div className={`h-full w-full flex flex-col bg-white ${embedded ? "" : "p-3"}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md mb-2 text-xs">
        <Globe size={13} className="text-[#E53935] shrink-0" />
        <span className="text-slate-700 font-mono truncate flex-1">{clean}</span>
        <a
          href={clean}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#E53935] hover:underline inline-flex items-center gap-1 shrink-0"
          data-testid="link-artifact-open-new-tab"
        >
          Open <ExternalLink size={11}/>
        </a>
      </div>
      <div className="flex-1 min-h-0 border border-slate-200 rounded-md overflow-hidden">
        <iframe
          title="external-artifact"
          src={embedSrc}
          className="w-full h-full border-0 block"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation"
          data-testid="link-artifact-iframe"
        />
      </div>
      {!isVideoEmbed && !isClaudeShare && (
        <p className="text-[10px] text-slate-500 mt-2 leading-snug">
          Note: some sites (notably Notion, Google Docs, paywalled pages) block embedding.
          If the page above is blank, learners can still use the <strong>Open</strong> link.
        </p>
      )}
    </div>
  );
}

function LinkArtifactPanel({ mod, onSaved }) {
  const [url, setUrl] = React.useState(mod.artifact_content || "");
  const [saving, setSaving] = React.useState(false);
  const dirty = (url || "").trim() !== (mod.artifact_content || "").trim();

  React.useEffect(() => { setUrl(mod.artifact_content || ""); }, [mod.artifact_content]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = normalizeArtifactUrl(url);
      await api.patch(`/modules/${mod.id}`, { artifact_content: clean });
      toast.success("Link saved");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save link");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full w-full flex flex-col bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
          <LinkIcon size={13}/> External link / embed URL
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="link-artifact-url-input"
            placeholder="https://claude.ai/share/... or https://youtu.be/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); }}
            className="bg-white font-mono text-sm"
          />
          <Button
            data-testid="save-link-artifact-button"
            onClick={save}
            disabled={!dirty || saving || !url.trim()}
            className="bg-[#E53935] hover:bg-[#c62828] text-white shrink-0"
          >
            {saving ? <Loader2 size={14} className="animate-spin"/> : "Save link"}
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Paste a public URL — Claude.ai shared artifact pages, YouTube/Vimeo videos, Loom recordings, Figma prototypes, Codepen demos, etc.
        </p>
      </div>
      <div className="flex-1 min-h-0 p-3 overflow-hidden">
        {mod.artifact_content
          ? <LinkArtifactView url={mod.artifact_content} embedded />
          : (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm text-center px-6">
              Save a URL above to preview the embed here.
            </div>
          )
        }
      </div>
    </div>
  );
}


// =====================================================================
// SourceEditor — inline + fullscreen editing of an artifact's source.
// Supports HTML / Markdown / Claude embed HTML / Claude JSX. (Not "link".)
// Keyboard: Cmd/Ctrl+S saves, Esc closes fullscreen modal.
// =====================================================================
function SourceEditor({ content, artifactType, onSave }) {
  const [draft, setDraft] = React.useState(content);
  const [saving, setSaving] = React.useState(false);
  const [fullscreen, setFullscreen] = React.useState(false);
  const dirty = draft !== content;

  // Keep the editor in sync when the upstream content changes (e.g. AI regenerated artifact)
  React.useEffect(() => {
    setDraft(content);
  }, [content]);

  const save = React.useCallback(async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await onSave(draft);
      toast.success("Source saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [dirty, saving, draft, onSave]);

  const discard = () => {
    setDraft(content);
    toast("Reverted to last saved version", { icon: "↩" });
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl+S → save
    if ((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "S")) {
      e.preventDefault();
      save();
      return;
    }
    // Tab key inserts a tab instead of moving focus
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = e.target;
      const { selectionStart: s, selectionEnd: ed } = ta;
      const next = draft.slice(0, s) + "  " + draft.slice(ed);
      setDraft(next);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 2; });
    }
  };

  // Warn before navigating away if there are unsaved changes
  React.useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const typeLabel = artifactType === "embed" ? "CLAUDE HTML" : artifactType === "jsx" ? "CLAUDE JSX" : artifactType;

  const editorBox = (insideModal) => (
    <div className={`flex flex-col bg-white ${insideModal ? "h-full" : "h-full"}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 font-mono">{typeLabel}</span>
          <span className="text-[10px] text-slate-400">·</span>
          <span className="text-[10px] text-slate-400 font-mono">{draft.length.toLocaleString()} chars</span>
          {dirty && (
            <span data-testid="source-dirty-badge" className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-[#E53935]/10 text-[#E53935] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E53935] animate-pulse"/>
              Unsaved
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => { navigator.clipboard.writeText(draft); toast.success("Copied"); }}
            className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200 rounded transition-colors"
            data-testid="copy-source-button"
            title="Copy"
          >
            <Copy size={13} />
          </button>
          <button
            onClick={discard}
            disabled={!dirty || saving}
            className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200 rounded transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:border-transparent"
            data-testid="discard-source-button"
            title="Discard"
          >
            <Undo2 size={13} />
          </button>
          {!insideModal && (
            <button
              onClick={() => setFullscreen(true)}
              className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200 rounded transition-colors"
              data-testid="fullscreen-source-button"
              title="Fullscreen edit"
            >
              <Maximize2 size={13} />
            </button>
          )}
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            className="h-7 bg-[#E53935] hover:bg-[#c62828] text-white text-xs px-3 disabled:opacity-50"
            data-testid="save-source-button"
          >
            {saving
              ? <><Loader2 size={12} className="animate-spin mr-1"/> Saving</>
              : <><Save size={12} className="mr-1"/> Save <kbd className="ml-1.5 text-[9px] opacity-70 font-mono">⌘S</kbd></>}
          </Button>
        </div>
      </div>

      {/* Editor textarea */}
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        spellCheck={false}
        wrap="soft"
        data-testid="source-editor-textarea"
        className="flex-1 w-full p-5 font-mono text-xs leading-relaxed text-slate-800 bg-white resize-none outline-none border-0 thin-scroll"
        placeholder={`Edit your ${artifactType} content here…`}
      />

      {/* Status footer */}
      <div className="px-3 py-1.5 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 font-mono flex items-center justify-between shrink-0">
        <span>Tab → 2 spaces · ⌘/Ctrl+S to save · Esc to exit fullscreen</span>
        <span>{draft.split(/\n/).length.toLocaleString()} lines</span>
      </div>
    </div>
  );

  return (
    <>
      {editorBox(false)}

      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent
          className="max-w-[95vw] w-[95vw] h-[92vh] p-0 gap-0 flex flex-col"
          onEscapeKeyDown={() => setFullscreen(false)}
          data-testid="source-fullscreen-dialog"
        >
          <DialogHeader className="px-4 py-3 border-b border-slate-200 flex-row items-center justify-between space-y-0 shrink-0">
            <DialogTitle className="text-sm font-display flex items-center gap-2">
              <Pencil size={13} className="text-[#E53935]"/> Edit module source
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            {editorBox(true)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// Block-like tags that the click handler may select inside an artifact (any depth).
// Includes container elements (section/div/main) so deeply-nested Claude cards are reachable.
const BLOCK_TAGS = new Set([
  "H1","H2","H3","H4","H5","H6","P","UL","OL","LI","BLOCKQUOTE",
  "SECTION","ARTICLE","ASIDE","HEADER","FOOTER","NAV","FIGURE","FIGCAPTION",
  "HR","TABLE","THEAD","TBODY","TR","TD","TH","PRE","DIV","MAIN",
  "IMG","VIDEO","IFRAME","CANVAS","BUTTON","FORM","LABEL","INPUT","DETAILS","SUMMARY",
]);

// =====================================================================
// WYSIWYG persistence helpers (embed / iframe mode)
//
// Claude HTML artifacts run their own <script> on load: they append nav
// buttons, fill containers from JS data arrays, toggle classes, etc.
// Serializing the live DOM therefore "bakes" runtime state into the saved
// HTML — on the next load the script runs again and duplicates / overwrites
// it, so the preview never matches what the admin saved.
//
// Fix: before the editor iframe loads we stamp every SOURCE element with a
// session id (data-uvc-i) using DOMParser (scripts don't execute there).
// User-created nodes get data-uvc-keep; user-touched nodes get
// data-uvc-edited. At save time we rebuild the document keeping only
// stamped/kept elements (runtime script output is dropped — the script
// recreates it on every load) and restore pristine attributes on elements
// the user never touched (so script-set state like quiz locks or active
// tabs never leaks into the saved file).
// =====================================================================
const UVC_EDIT_ATTRS = [
  "data-uvc-i", "data-uvc-keep", "data-uvc-edited", "data-uvc-script-touched",
  "data-uvc-selected", "data-uvc-multi-selected", "data-uvc-interactive-hint",
];

// Runs inside the editor iframe BEFORE the artifact's own scripts: flags any
// stamped element whose content gets rewritten during the initial render
// (live counters, progress labels, data-driven regions) so the editor can
// warn that edits there will be overwritten on the next load.
const UVC_TOUCH_OBSERVER = `(function(){
  var mo = new MutationObserver(function(muts){
    for (var i = 0; i < muts.length; i++) {
      var m = muts[i];
      if (m.type === 'childList') {
        // The HTML parser itself appends stamped elements / text nodes while
        // the document streams in — only script activity removes nodes or
        // inserts UNstamped elements.
        var suspicious = m.removedNodes.length > 0;
        if (!suspicious) {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var a = m.addedNodes[j];
            if (a.nodeType === 1 && !a.hasAttribute('data-uvc-i')) { suspicious = true; break; }
          }
        }
        if (!suspicious) continue;
      }
      var t = m.target;
      if (t && t.nodeType === 3) t = t.parentElement;
      while (t && t.nodeType === 1) {
        if (t.hasAttribute && t.hasAttribute('data-uvc-i')) { t.setAttribute('data-uvc-script-touched', '1'); break; }
        t = t.parentElement;
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, characterData: true, subtree: true });
  window.addEventListener('load', function(){ setTimeout(function(){ mo.disconnect(); }, 400); });
})();`;

// Strip any pre-baked button / option children inside the slideshow's nav
// containers. The artifact's own inline script always builds them at runtime;
// if a stale save persisted those runtime children we'd render them PLUS the
// freshly-built set on every load (the "repeating 01..10 rail" bug). Applied
// BEFORE stamping so the pristine baseline carries empty containers and the
// save serializer can never re-introduce the duplicates.
function scrubRuntimeRailContainers(src) {
  if (!src) return src || "";
  return src
    .replace(/(<nav\b[^>]*id=["']railList["'][^>]*>)[\s\S]*?(<\/nav>)/i, "$1$2")
    .replace(/(<select\b[^>]*id=["']mobileSel["'][^>]*>)[\s\S]*?(<\/select>)/i, "$1$2");
}

// Parse a full HTML document string and stamp every element with a stable
// per-session identity. DOMParser never executes scripts, so stamps land
// exclusively on true source elements.
function stampSourceDocument(src) {
  const s = scrubRuntimeRailContainers((src || "").trim());
  if (!s) return src || "";
  const doc = new DOMParser().parseFromString(s, "text/html");
  let i = 0;
  doc.querySelectorAll("*").forEach((el) => el.setAttribute("data-uvc-i", String(i++)));
  return "<!doctype html>\n" + doc.documentElement.outerHTML;
}

// Editor-only instrumentation layered on top of the stamped baseline. Kept
// OUT of the baseline string so the save pipeline (pristine rescue) can
// never resurrect it into the persisted HTML.
function instrumentForEditing(stampedHtml) {
  const s = (stampedHtml || "").trim();
  if (!s) return stampedHtml || "";
  const doc = new DOMParser().parseFromString(s, "text/html");
  const obs = doc.createElement("script");
  obs.textContent = UVC_TOUCH_OBSERVER;
  doc.head.insertBefore(obs, doc.head.firstChild);
  return "<!doctype html>\n" + doc.documentElement.outerHTML;
}

// Mark a freshly user-inserted element (and its subtree) as content to keep.
function stampKeepSubtree(el) {
  if (!el || el.nodeType !== 1) return;
  if (!el.hasAttribute("data-uvc-i")) el.setAttribute("data-uvc-keep", "1");
  el.querySelectorAll("*").forEach((d) => {
    if (!d.hasAttribute("data-uvc-i")) d.setAttribute("data-uvc-keep", "1");
  });
}

// After any contentEditable input / execCommand, flag the block containing
// the caret as user-edited and adopt any new unstamped nodes the edit
// created (Enter-paragraphs, pasted markup, execCommand wrappers, ...).
function markUserEditRegion(root) {
  try {
    const doc = root.ownerDocument || document;
    const ws = (doc.defaultView || window).getSelection?.();
    let n = ws && ws.anchorNode;
    if (n && n.nodeType === Node.TEXT_NODE) n = n.parentElement;
    if (!n || n === root || !root.contains(n)) return;
    let cur = n;
    while (cur && cur !== root && !cur.hasAttribute("data-uvc-i") && !cur.hasAttribute("data-uvc-keep")) {
      cur.setAttribute("data-uvc-keep", "1");
      cur = cur.parentElement;
    }
    const block = cur && cur !== root ? cur : n;
    if (block && block !== root) {
      block.setAttribute("data-uvc-edited", "1");
      block.querySelectorAll("*").forEach((d) => {
        if (!d.hasAttribute("data-uvc-i") && !d.hasAttribute("data-uvc-keep")) d.setAttribute("data-uvc-keep", "1");
      });
    }
  } catch { /* selection may be unavailable */ }
}

function stripUvcInternalAttrs(rootEl) {
  if (!rootEl || rootEl.nodeType !== 1) return;
  [rootEl, ...rootEl.querySelectorAll("*")].forEach((el) => {
    UVC_EDIT_ATTRS.forEach((a) => el.removeAttribute(a));
  });
}

// Rebuild a clean source document from the live (script-mutated) editor DOM.
function serializeEditedIframe(liveDoc, stampedSource) {
  const pristine = new DOMParser().parseFromString(stampedSource, "text/html");
  const pristineMap = new Map();
  pristine.querySelectorAll("[data-uvc-i]").forEach((el) => pristineMap.set(el.getAttribute("data-uvc-i"), el));

  const outDoc = document.implementation.createHTMLDocument("");
  const outHtml = outDoc.documentElement;
  while (outHtml.attributes.length > 0) outHtml.removeAttribute(outHtml.attributes[0].name);
  Array.from(pristine.documentElement.attributes).forEach((a) => {
    if (!a.name.startsWith("data-uvc-")) outHtml.setAttribute(a.name, a.value);
  });
  // NOTE: must remove children via removeChild — setting innerHTML="" on a
  // documentElement re-creates an implied empty <head> + <body> pair.
  while (outHtml.firstChild) outHtml.removeChild(outHtml.firstChild);

  const visit = (liveParent, outParent) => {
    Array.from(liveParent.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.COMMENT_NODE) {
        outParent.appendChild(outDoc.importNode(node, true));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const stamp = node.getAttribute("data-uvc-i");
      const kept = node.hasAttribute("data-uvc-keep");
      if (stamp == null && !kept) return; // runtime/script-created → script rebuilds it on load
      const edited = node.hasAttribute("data-uvc-edited");
      const prisEl = stamp != null ? pristineMap.get(stamp) : null;

      // <template> content is inert (never stamped) — keep the pristine copy whole.
      if (node.tagName === "TEMPLATE") {
        outParent.appendChild(outDoc.importNode(prisEl || node, true));
        return;
      }
      // Script-rebuilt container rescue: the source had child elements but a
      // script replaced them all at runtime (and the user never touched the
      // region) — keep the original source subtree instead of emptying it.
      if (prisEl && !edited && prisEl.children.length > 0) {
        const liveKept = Array.from(node.children).some(
          (c) => c.hasAttribute("data-uvc-i") || c.hasAttribute("data-uvc-keep")
        );
        const touched = node.querySelector("[data-uvc-edited],[data-uvc-keep]") != null;
        if (!liveKept && !touched) {
          outParent.appendChild(outDoc.importNode(prisEl, true));
          return;
        }
      }
      const outEl = prisEl && !edited
        ? outDoc.importNode(prisEl, false)  // untouched → pristine attributes (no baked script state)
        : outDoc.importNode(node, false);   // user-edited / user-created → live attributes
      outParent.appendChild(outEl);
      visit(node, outEl);
    });
  };
  visit(liveDoc.documentElement, outHtml);

  stripUvcInternalAttrs(outHtml);
  const outBody = outDoc.body;
  if (outBody) {
    outBody.removeAttribute("contenteditable");
    outBody.removeAttribute("data-uvc-interact-mode");
    // setupRoot applies outline:none to the live editing root — never persist it.
    if (outBody.style) {
      outBody.style.removeProperty("outline");
      if (!outBody.getAttribute("style")) outBody.removeAttribute("style");
    }
  }
  return "<!doctype html>\n" + outHtml.outerHTML;
}

// Fonts users can apply to selected text / blocks from inside the visual editor.
const FONT_CHOICES = [
  { id: "default",  label: "Default",         css: "" },
  { id: "manrope",  label: "Manrope",         css: "'Manrope', 'IBM Plex Sans', sans-serif" },
  { id: "inter",    label: "Inter",           css: "'Inter', system-ui, sans-serif" },
  { id: "system",   label: "System Sans",     css: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif" },
  { id: "georgia",  label: "Georgia (Serif)", css: "Georgia, 'Times New Roman', serif" },
  { id: "playfair", label: "Playfair Display",css: "'Playfair Display', 'Times New Roman', serif" },
  { id: "mono",     label: "JetBrains Mono",  css: "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace" },
];

// Curated color palette for text & background formatting. "" = reset.
const COLOR_PALETTE = [
  { id: "reset",      label: "Reset",       css: "" },
  { id: "ink",        label: "Ink",         css: "#222222" },
  { id: "slate",      label: "Slate",       css: "#475569" },
  { id: "white",      label: "White",       css: "#ffffff" },
  { id: "cream",      label: "Cream",       css: "#f8f5ef" },
  { id: "brand-red",  label: "Brand red",   css: "#E53935" },
  { id: "deep-black", label: "Deep black",  css: "#0a0a0a" },
  { id: "emerald",    label: "Emerald",     css: "#059669" },
  { id: "amber",      label: "Amber",       css: "#d97706" },
  { id: "sapphire",   label: "Sapphire",    css: "#1d4ed8" },
  { id: "rose",       label: "Rose",        css: "#be185d" },
  { id: "stone",      label: "Stone",       css: "#a8a29e" },
];

// Text-size presets exposed in the Format popover.
const TEXT_SIZES = [
  { id: "xs",      label: "Tiny",     css: "12px" },
  { id: "sm",      label: "Small",    css: "14px" },
  { id: "default", label: "Default",  css: "" },
  { id: "lg",      label: "Large",    css: "20px" },
  { id: "xl",      label: "Heading",  css: "28px" },
  { id: "xxl",     label: "Display",  css: "44px" },
];

// Heading presets — wraps the current block in the chosen tag.
const HEADING_CHOICES = [
  { id: "p",  label: "Paragraph", tag: "P" },
  { id: "h1", label: "Heading 1", tag: "H1" },
  { id: "h2", label: "Heading 2", tag: "H2" },
  { id: "h3", label: "Heading 3", tag: "H3" },
  { id: "h4", label: "Heading 4", tag: "H4" },
];

// =====================================================================
// VisualEditor — click-to-edit text + hover/select controls on blocks.
// Supports: html, markdown (converts to html on save), embed (Claude HTML in iframe).
// Not supported: jsx, link.
// =====================================================================
// Forward-ref VisualEditor so the parent can call its `flushSave()` before
// switching tabs (otherwise unsaved iframe-mode DOM edits get lost on unmount).
const VisualEditor = React.forwardRef(function VisualEditor({ content, artifactType, onSave }, fwdRef) {
  const isUnsupported = artifactType === "jsx" || artifactType === "link";
  const isIframeMode = artifactType === "embed";
  const isInlineMode = artifactType === "html" || artifactType === "markdown";

  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [tick, setTick] = React.useState(0); // bumps when selection/DOM changes
  const [insertOpen, setInsertOpen] = React.useState(false);
  const [mediaPicker, setMediaPicker] = React.useState(null); // {kind: 'image'|'video'} or null
  // Marquee drag-select state (inline mode only)
  const [marqueeMode, setMarqueeMode] = React.useState(false);
  const [marqueeRect, setMarqueeRect] = React.useState(null); // {left, top, width, height} in scroll-content coords
  const [floatingPos, setFloatingPos] = React.useState(null); // {top, left} in scroll-content coords
  // Resize-handles overlay state: scroll-content coords + bounding box of the
  // single selected element, when that element is an image or sized block.
  const [resizeRect, setResizeRect] = React.useState(null);
  // True while user is actively dragging one of the 8 handles
  const draggingHandleRef = React.useRef(null);
  const [fontOpen, setFontOpen] = React.useState(false);
  const [colorOpen, setColorOpen] = React.useState(false);
  const [formatOpen, setFormatOpen] = React.useState(false);
  const [sectionsOpen, setSectionsOpen] = React.useState(false);
  // Interact mode lets the admin click through buttons/links inside the artifact
  // (e.g. a Claude "Next slide" button) without contentEditable swallowing the click.
  const [interactMode, setInteractMode] = React.useState(false);
  const editorScrollRef = React.useRef(null);
  // Outer wrapper that contains EITHER the iframe or the inline editor.
  // Used as the positioning context for the floating block controls and the
  // resize-handles overlay, so they work in both iframe and inline modes.
  const editorWrapRef = React.useRef(null);
  const multiSelectedRef = React.useRef([]); // direct children selected via marquee
  const iframeRef = React.useRef(null);
  const inlineRef = React.useRef(null);
  const replaceImageInputRef = React.useRef(null); // hidden <input type=file> for image replacement
  const selectedRef = React.useRef(null); // direct child of root that is selected
  const rootRef = React.useRef(null); // the contentEditable root (body inside iframe, or div inline)
  // Undo / Redo history of root.innerHTML snapshots (capped at MAX_HISTORY)
  const historyRef = React.useRef({ past: [], future: [] });
  const [historyTick, setHistoryTick] = React.useState(0); // re-render undo/redo buttons when stacks change
  const MAX_HISTORY = 60;

  // Snapshot the editable root's innerHTML with selection markers stripped,
  // so undo never restores stale selection outlines.
  const snapshotHTML = React.useCallback(() => {
    const root = rootRef.current;
    if (!root) return null;
    const clone = root.cloneNode(true);
    clone.querySelectorAll("[data-uvc-selected],[data-uvc-multi-selected]").forEach((n) => {
      n.removeAttribute("data-uvc-selected");
      n.removeAttribute("data-uvc-multi-selected");
    });
    return clone.innerHTML;
  }, []);

  // Call before any structural mutation (move / duplicate / delete / font change).
  const pushHistory = React.useCallback(() => {
    const snap = snapshotHTML();
    if (snap == null) return;
    const past = historyRef.current.past;
    if (past.length > 0 && past[past.length - 1] === snap) return; // no-op change
    past.push(snap);
    while (past.length > MAX_HISTORY) past.shift();
    historyRef.current.future = [];
    setHistoryTick((t) => t + 1);
  }, [snapshotHTML]);

  const restoreSnapshot = React.useCallback((html) => {
    const root = rootRef.current;
    if (!root || html == null) return;
    root.innerHTML = html;
    selectedRef.current = null;
    multiSelectedRef.current = [];
    setDirty(true);
    setTick((t) => t + 1);
  }, []);

  const undo = React.useCallback(() => {
    const { past, future } = historyRef.current;
    if (past.length === 0) { toast.message("Nothing to undo"); return; }
    const current = snapshotHTML();
    const previous = past.pop();
    if (current != null) future.push(current);
    restoreSnapshot(previous);
    setHistoryTick((t) => t + 1);
  }, [restoreSnapshot, snapshotHTML]);

  const redo = React.useCallback(() => {
    const { past, future } = historyRef.current;
    if (future.length === 0) { toast.message("Nothing to redo"); return; }
    const current = snapshotHTML();
    const next = future.pop();
    if (current != null) past.push(current);
    restoreSnapshot(next);
    setHistoryTick((t) => t + 1);
  }, [restoreSnapshot, snapshotHTML]);

  // Refs so setupRoot's static onKey handler can reach the latest undo / redo functions.
  const undoRef = React.useRef(undo);
  const redoRef = React.useRef(redo);
  React.useEffect(() => { undoRef.current = undo; redoRef.current = redo; }, [undo, redo]);

  // Refs for Word-style text formatting hotkeys (Ctrl+B/I/U/K).
  // Initialized to no-ops; the actual handlers are wired up after they're defined.
  const boldRef = React.useRef(() => {});
  const italicRef = React.useRef(() => {});
  const underlineRef = React.useRef(() => {});
  const linkRef = React.useRef(() => {});

  // Compute the editable HTML on first mount only (markdown → html)
  const initialEditableHtml = React.useMemo(() => {
    if (artifactType === "markdown") return mdToHtml(content);
    // Embed mode: stamp every source element so save can tell true source
    // content apart from runtime nodes created by the artifact's own scripts.
    if (artifactType === "embed") return stampSourceDocument(content);
    return content;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // What the editor iframe actually loads: stamped baseline + editor-only
  // instrumentation (runtime-mutation observer). Never used for saving.
  const editorSrcDoc = React.useMemo(
    () => (artifactType === "embed" ? instrumentForEditing(initialEditableHtml) : initialEditableHtml),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const setupRoot = React.useCallback((root) => {
    if (!root) return undefined;
    rootRef.current = root;
    root.setAttribute("contenteditable", "true");
    root.style.outline = "none";    const onClick = (e) => {
      let node = e.target;
      // Walk up from the click target to the nearest block-like element so deeply-nested
      // artifacts (Claude sections, dark cards, etc.) can be reached, not just root's direct children.
      while (node && node !== root && !BLOCK_TAGS.has(node.tagName)) {
        node = node.parentElement;
      }
      if (!node || node === root) return;
      // Clear any previous selection marker (single + multi)
      root.querySelectorAll("[data-uvc-selected],[data-uvc-multi-selected]").forEach((n) => {
        n.removeAttribute("data-uvc-selected");
        n.removeAttribute("data-uvc-multi-selected");
      });
      multiSelectedRef.current = [];
      node.setAttribute("data-uvc-selected", "1");
      selectedRef.current = node;
      setTick((t) => t + 1);
    };
    const onInput = () => {
      markUserEditRegion(root);
      setDirty(true);
    };
    const onKey = (e) => {
      // ⌘/Ctrl+Shift+Z → redo  (must come BEFORE the plain ⌘/Ctrl+Z check)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        redoRef.current?.();
        return;
      }
      // ⌘/Ctrl+Y → redo
      if ((e.metaKey || e.ctrlKey) && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redoRef.current?.();
        return;
      }
      // ⌘/Ctrl+Z → undo
      if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        undoRef.current?.();
        return;
      }
      // Bold / Italic / Underline / Link — Word-style text formatting shortcuts
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === "b" || e.key === "B")) {
        e.preventDefault();
        boldRef.current?.();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === "i" || e.key === "I")) {
        e.preventDefault();
        italicRef.current?.();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && (e.key === "u" || e.key === "U")) {
        e.preventDefault();
        underlineRef.current?.();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        linkRef.current?.();
        return;
      }
      // ⌘/Ctrl+S → save
      if ((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        const el = document.querySelector("[data-testid='save-visual-button']");
        el?.click();
      }
      // Delete/Backspace on whole block when block selected & nothing typed inside
      if (e.key === "Backspace" || e.key === "Delete") {
        // Only auto-delete a block if it's currently empty (user explicitly emptied it)
        const sel = selectedRef.current;
        if (sel && sel.textContent.trim() === "" && (e.key === "Delete")) {
          e.preventDefault();
          performAction("delete");
        }
      }
    };

    root.addEventListener("click", onClick);
    root.addEventListener("input", onInput);
    root.addEventListener("keydown", onKey);
    return () => {
      root.removeEventListener("click", onClick);
      root.removeEventListener("input", onInput);
      root.removeEventListener("keydown", onKey);
    };
  }, []);

  // Strip a full <!doctype>/<html>/<head>/<body> document down to just its body innerHTML
  // so direct children of the editable root are real content blocks, not <head>/<script>/<title>.
  const sanitizeForInline = React.useCallback((src) => {
    const s = (src || "").trim();
    if (!s) return s;
    if (!/<!doctype|<html|<head|<body/i.test(s)) return s;
    try {
      const parsed = new DOMParser().parseFromString(s, "text/html");
      const body = parsed?.body;
      return body ? body.innerHTML : s;
    } catch {
      return s;
    }
  }, []);

  // Inline mode: use a callback ref so we set innerHTML exactly once on mount.
  // (Using React's dangerouslySetInnerHTML caused re-renders to wipe DOM edits.)
  const setInlineRef = React.useCallback((el) => {
    inlineRef.current = el;
    if (!el) return;
    if (!el.dataset.uvcInit) {
      el.innerHTML = sanitizeForInline(initialEditableHtml);
      el.dataset.uvcInit = "1";
      setupRoot(el);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupRoot, sanitizeForInline]);

  // Inline mode: legacy useEffect kept as a no-op safety net
  React.useEffect(() => {
    if (!isInlineMode) return undefined;
    if (inlineRef.current && !inlineRef.current.dataset.uvcInit) {
      inlineRef.current.innerHTML = sanitizeForInline(initialEditableHtml);
      inlineRef.current.dataset.uvcInit = "1";
      return setupRoot(inlineRef.current);
    }
    return undefined;
  }, [isInlineMode, setupRoot, initialEditableHtml, sanitizeForInline]);

  // Iframe mode: attach handlers once the iframe document is ready
  React.useEffect(() => {
    if (!isIframeMode) return undefined;
    const iframe = iframeRef.current;
    if (!iframe) return undefined;
    let cleanup;
    const init = () => {
      const doc = iframe.contentDocument;
      if (!doc || !doc.body) return;
      // Inject selection-highlight styles into iframe head
      if (!doc.getElementById("uvc-edit-style")) {
        const s = doc.createElement("style");
        s.id = "uvc-edit-style";
        s.textContent = `
          body[contenteditable="true"] { caret-color: #E53935; }
          body [data-uvc-selected],
          body [data-uvc-multi-selected] {
            outline: 2px solid #E53935 !important;
            outline-offset: 4px !important;
            border-radius: 4px !important;
            position: relative;
          }
          body [data-uvc-multi-selected] {
            background-color: rgba(229, 57, 53, 0.06) !important;
          }
          body [data-uvc-interactive] {
            outline: 2px dashed #1d4ed8 !important;
            outline-offset: 6px !important;
          }
          body[data-uvc-interact-mode="1"] [data-uvc-selected],
          body[data-uvc-interact-mode="1"] [data-uvc-multi-selected] { outline: none !important; }
        `;
        doc.head.appendChild(s);
      }
      cleanup = setupRoot(doc.body);
    };
    if (iframe.contentDocument?.body) init();
    iframe.addEventListener("load", init);
    return () => {
      iframe.removeEventListener("load", init);
      if (cleanup) cleanup();
    };
  }, [isIframeMode, setupRoot]);

  function performAction(action) {
    const multi = (multiSelectedRef.current || []).filter((el) => el && el.isConnected);
    if (multi.length > 1) {
      pushHistory();
      // Sort in document order
      const sorted = multi.slice().sort((a, b) => {
        const pos = a.compareDocumentPosition(b);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      const parent = first.parentElement;
      if (!parent) return;
      if (action === "up") {
        const prev = first.previousElementSibling;
        if (prev && !multi.includes(prev)) {
          parent.insertBefore(prev, last.nextElementSibling);
        }
      } else if (action === "down") {
        const next = last.nextElementSibling;
        if (next && !multi.includes(next)) {
          parent.insertBefore(next, first);
        }
      } else if (action === "duplicate") {
        sorted.slice().reverse().forEach((el) => {
          const clone = el.cloneNode(true);
          clone.removeAttribute("data-uvc-selected");
          clone.removeAttribute("data-uvc-multi-selected");
          parent.insertBefore(clone, el.nextElementSibling);
        });
      } else if (action === "delete") {
        const after = last.nextElementSibling || first.previousElementSibling;
        sorted.forEach((el) => el.remove());
        parent.setAttribute("data-uvc-edited", "1"); // user emptied this container — keep its live state
        multiSelectedRef.current = [];
        selectedRef.current = after || null;
        if (after) after.setAttribute("data-uvc-selected", "1");
      }
      setDirty(true);
      setTick((t) => t + 1);
      return;
    }
    const sel = selectedRef.current;
    if (!sel) return;
    const parent = sel.parentElement;
    if (!parent) return;
    pushHistory();
    if (action === "up") {
      const prev = sel.previousElementSibling;
      if (prev) parent.insertBefore(sel, prev);
    } else if (action === "down") {
      const next = sel.nextElementSibling;
      if (next) parent.insertBefore(next, sel);
    } else if (action === "duplicate") {
      const clone = sel.cloneNode(true);
      clone.removeAttribute("data-uvc-selected");
      parent.insertBefore(clone, sel.nextElementSibling);
    } else if (action === "delete") {
      const next = sel.nextElementSibling || sel.previousElementSibling;
      sel.remove();
      parent.setAttribute("data-uvc-edited", "1"); // user emptied this container — keep its live state
      selectedRef.current = next || null;
      if (next) next.setAttribute("data-uvc-selected", "1");
    }
    setDirty(true);
    setTick((t) => t + 1);
  }

  // Climb the DOM tree to select the parent of the current selection.
  // Lets users walk up from a deeply-nested element (e.g. an <h2> inside a styled card)
  // to its container so they can delete or reorder the wrapping section.
  function selectParent() {
    const root = rootRef.current;
    const sel = selectedRef.current;
    if (!root || !sel) return;
    const parent = sel.parentElement;
    if (!parent || parent === root) {
      toast.info("Already at top-level block");
      return;
    }
    root.querySelectorAll("[data-uvc-selected],[data-uvc-multi-selected]").forEach((n) => {
      n.removeAttribute("data-uvc-selected");
      n.removeAttribute("data-uvc-multi-selected");
    });
    multiSelectedRef.current = [];
    parent.setAttribute("data-uvc-selected", "1");
    selectedRef.current = parent;
    try { parent.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch { /* ignore */ }
    setTick((t) => t + 1);
  }

  // Apply a font-family to the highlighted text range (if any), else to the selected block(s).
  // Pass fontCss="" to remove the font override.
  function applyFont(fontCss) {
    const root = rootRef.current;
    if (!root) return;
    const doc = root.ownerDocument || document;
    const win = doc.defaultView || window;
    const winSel = win.getSelection?.();

    // 1) If the user has a non-collapsed text range inside the editor, wrap that range.
    if (winSel && winSel.rangeCount > 0 && !winSel.isCollapsed) {
      const range = winSel.getRangeAt(0);
      if (root.contains(range.commonAncestorContainer)) {
        pushHistory();
        if (!fontCss) {
          // Unwrap any [data-uvc-font] spans that fully cover the selection
          root.querySelectorAll("[data-uvc-font]").forEach((s) => {
            if (range.intersectsNode(s)) {
              while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
              s.remove();
            }
          });
        } else {
          const span = doc.createElement("span");
          span.setAttribute("data-uvc-font", "1");
          span.setAttribute("data-uvc-keep", "1");
          span.style.fontFamily = fontCss;
          try {
            range.surroundContents(span);
          } catch {
            const contents = range.extractContents();
            span.appendChild(contents);
            range.insertNode(span);
          }
        }
        setDirty(true);
        setTick((t) => t + 1);
        return;
      }
    }

    // 2) Else apply to the multi-selection or single selected block.
    const targets = (multiSelectedRef.current || []).filter((el) => el && el.isConnected);
    const list = targets.length ? targets : (selectedRef.current && selectedRef.current.isConnected ? [selectedRef.current] : []);
    if (list.length === 0) {
      toast.message("Select a block or highlight text first");
      return;
    }
    pushHistory();
    for (const el of list) {
      el.setAttribute("data-uvc-edited", "1");
      if (fontCss) el.style.fontFamily = fontCss;
      else el.style.removeProperty("font-family");
    }
    setDirty(true);
    setTick((t) => t + 1);
  }

  // Apply a CSS color value to the highlighted text range (foreground or background),
  // else to the selected block(s). Pass colorCss="" to remove the override.
  // mode: "text" → color / "bg" → backgroundColor.
  function applyColor(colorCss, mode) {
    const root = rootRef.current;
    if (!root) return;
    const doc = root.ownerDocument || document;
    const win = doc.defaultView || window;
    const winSel = win.getSelection?.();
    const cssProp = mode === "bg" ? "backgroundColor" : "color";
    const datatag = mode === "bg" ? "data-uvc-bg" : "data-uvc-color";

    // 1) Wrap a text range
    if (winSel && winSel.rangeCount > 0 && !winSel.isCollapsed) {
      const range = winSel.getRangeAt(0);
      if (root.contains(range.commonAncestorContainer)) {
        pushHistory();
        if (!colorCss) {
          // Unwrap any spans of this kind that intersect the selection
          root.querySelectorAll(`[${datatag}]`).forEach((s) => {
            if (range.intersectsNode(s)) {
              while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
              s.remove();
            }
          });
        } else {
          const span = doc.createElement("span");
          span.setAttribute(datatag, "1");
          span.setAttribute("data-uvc-keep", "1");
          span.style[cssProp] = colorCss;
          try {
            range.surroundContents(span);
          } catch {
            const contents = range.extractContents();
            span.appendChild(contents);
            range.insertNode(span);
          }
        }
        setDirty(true);
        setTick((t) => t + 1);
        return;
      }
    }

    // 2) Apply to selected block(s)
    const targets = (multiSelectedRef.current || []).filter((el) => el && el.isConnected);
    const list = targets.length ? targets : (selectedRef.current && selectedRef.current.isConnected ? [selectedRef.current] : []);
    if (list.length === 0) {
      toast.message("Select a block or highlight text first");
      return;
    }
    pushHistory();
    for (const el of list) {
      el.setAttribute("data-uvc-edited", "1");
      if (colorCss) el.style[cssProp] = colorCss;
      else el.style.removeProperty(mode === "bg" ? "background-color" : "color");
    }
    setDirty(true);
    setTick((t) => t + 1);
  }

  // Trigger the hidden file picker so the user can swap the selected <img>'s source.
  function triggerReplaceImage() {
    const sel = selectedRef.current;
    if (!sel || sel.tagName !== "IMG") {
      toast.message("Click an image to replace it");
      return;
    }
    if (replaceImageInputRef.current) {
      replaceImageInputRef.current.value = ""; // allow re-uploading the same file
      replaceImageInputRef.current.click();
    }
  }

  // Upload the chosen file and swap the src on the selected <img>.
  async function handleReplaceImageFile(file) {
    const target = selectedRef.current;
    if (!target || target.tagName !== "IMG" || !file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload-media", form, { headers: { "Content-Type": "multipart/form-data" } });
      // Store the relative URL only — the iframe renderer absolutizes against
      // the current env's backend at view time, so the same artifact survives
      // being moved between preview ↔ production.
      pushHistory();
      target.setAttribute("data-uvc-edited", "1");
      target.src = data.url;
      if (file.name) target.alt = file.name;
      setDirty(true);
      setTick((t) => t + 1);
      toast.success("Image replaced");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image upload failed");
    }
  }

  // ===== Word-style text formatting =====
  // Most operations use the host document's execCommand which still works
  // reliably in all evergreen browsers; we capture history before each call.
  function execCmd(cmd, value = null) {
    const root = rootRef.current;
    if (!root) return;
    const doc = root.ownerDocument || document;
    pushHistory();
    try { doc.execCommand(cmd, false, value); } catch { /* ignore */ }
    markUserEditRegion(root);
    setDirty(true);
    setTick((t) => t + 1);
  }

  // If no text range exists, select the entire selected block so execCommand
  // has something to operate on (bold a whole heading just by clicking it).
  function ensureTextSelection() {
    const root = rootRef.current;
    if (!root) return false;
    const doc = root.ownerDocument || document;
    const win = doc.defaultView || window;
    const ws = win.getSelection?.();
    if (!ws) return false;
    if (ws.rangeCount > 0 && !ws.isCollapsed && root.contains(ws.anchorNode)) return true;
    const sel = selectedRef.current;
    if (!sel || !root.contains(sel)) return false;
    const r = doc.createRange();
    r.selectNodeContents(sel);
    ws.removeAllRanges();
    ws.addRange(r);
    return true;
  }

  const fmtBold = () => {
    if (!ensureTextSelection()) return;
    const root = rootRef.current;
    const doc = root?.ownerDocument || document;
    const winSel = (doc.defaultView || window).getSelection?.();
    // If the selection just spans an entire block (we expanded it), prefer an
    // inline-style toggle so headings (which inherit bold from their tag) flip
    // correctly instead of becoming `font-weight: normal`.
    const sel = selectedRef.current;
    const wholeBlock = sel && winSel && winSel.toString().length > 0 && winSel.toString().trim() === (sel.textContent || "").trim();
    if (wholeBlock) {
      pushHistory();
      sel.setAttribute("data-uvc-edited", "1");
      const cur = sel.style.fontWeight || "";
      if (cur === "bold" || cur === "700") sel.style.removeProperty("font-weight");
      else sel.style.fontWeight = "bold";
      setDirty(true);
      setTick((t) => t + 1);
      return;
    }
    execCmd("bold");
  };
  const fmtItalic = () => {
    if (!ensureTextSelection()) return;
    const root = rootRef.current;
    const doc = root?.ownerDocument || document;
    const winSel = (doc.defaultView || window).getSelection?.();
    const sel = selectedRef.current;
    const wholeBlock = sel && winSel && winSel.toString().length > 0 && winSel.toString().trim() === (sel.textContent || "").trim();
    if (wholeBlock) {
      pushHistory();
      sel.setAttribute("data-uvc-edited", "1");
      const cur = sel.style.fontStyle || "";
      if (cur === "italic") sel.style.removeProperty("font-style");
      else sel.style.fontStyle = "italic";
      setDirty(true);
      setTick((t) => t + 1);
      return;
    }
    execCmd("italic");
  };
  const fmtUnderline = () => {
    if (!ensureTextSelection()) return;
    const root = rootRef.current;
    const doc = root?.ownerDocument || document;
    const winSel = (doc.defaultView || window).getSelection?.();
    const sel = selectedRef.current;
    const wholeBlock = sel && winSel && winSel.toString().length > 0 && winSel.toString().trim() === (sel.textContent || "").trim();
    if (wholeBlock) {
      pushHistory();
      sel.setAttribute("data-uvc-edited", "1");
      const cur = sel.style.textDecoration || "";
      if (cur.includes("underline")) sel.style.removeProperty("text-decoration");
      else sel.style.textDecoration = "underline";
      setDirty(true);
      setTick((t) => t + 1);
      return;
    }
    execCmd("underline");
  };
  const fmtAlign = (dir) => {
    ensureTextSelection();
    const cmd = { left: "justifyLeft", center: "justifyCenter", right: "justifyRight", justify: "justifyFull" }[dir];
    if (cmd) execCmd(cmd);
  };
  const fmtList = (kind) => {
    ensureTextSelection();
    execCmd(kind === "ordered" ? "insertOrderedList" : "insertUnorderedList");
  };
  const fmtHeading = (tag) => {
    ensureTextSelection();
    execCmd("formatBlock", `<${tag.toLowerCase()}>`);
  };
  const fmtLink = () => {
    const root = rootRef.current;
    if (!root) return;
    const url = window.prompt("Enter the URL:", "https://");
    if (!url) return;
    ensureTextSelection();
    execCmd("createLink", url);
    // Make the link visually distinct
    const doc = root.ownerDocument || document;
    doc.querySelectorAll("a[href]").forEach((a) => {
      if (!a.style.color) { a.style.color = "#E53935"; a.style.textDecoration = "underline"; a.setAttribute("data-uvc-edited", "1"); }
    });
  };
  const fmtClear = () => { ensureTextSelection(); execCmd("removeFormat"); };

  // Keep the keyboard-shortcut refs pointing at the latest closures
  React.useEffect(() => {
    boldRef.current = fmtBold;
    italicRef.current = fmtItalic;
    underlineRef.current = fmtUnderline;
    linkRef.current = fmtLink;
  });

  // Apply a font-size preset (e.g. "20px"). Same dual flow as applyFont/applyColor:
  // text range → wrap span; block selection → inline style.fontSize.
  function applyTextSize(sizeCss) {
    const root = rootRef.current;
    if (!root) return;
    const doc = root.ownerDocument || document;
    const win = doc.defaultView || window;
    const winSel = win.getSelection?.();

    if (winSel && winSel.rangeCount > 0 && !winSel.isCollapsed) {
      const range = winSel.getRangeAt(0);
      if (root.contains(range.commonAncestorContainer)) {
        pushHistory();
        if (!sizeCss) {
          root.querySelectorAll("[data-uvc-size]").forEach((s) => {
            if (range.intersectsNode(s)) {
              while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
              s.remove();
            }
          });
        } else {
          const span = doc.createElement("span");
          span.setAttribute("data-uvc-size", "1");
          span.setAttribute("data-uvc-keep", "1");
          span.style.fontSize = sizeCss;
          try { range.surroundContents(span); }
          catch { const c = range.extractContents(); span.appendChild(c); range.insertNode(span); }
        }
        setDirty(true);
        setTick((t) => t + 1);
        return;
      }
    }
    const targets = (multiSelectedRef.current || []).filter((el) => el && el.isConnected);
    const list = targets.length ? targets : (selectedRef.current && selectedRef.current.isConnected ? [selectedRef.current] : []);
    if (list.length === 0) { toast.message("Select a block or highlight text first"); return; }
    pushHistory();
    for (const el of list) {
      el.setAttribute("data-uvc-edited", "1");
      if (sizeCss) el.style.fontSize = sizeCss;
      else el.style.removeProperty("font-size");
    }
    setDirty(true);
    setTick((t) => t + 1);
  }

  // Apply max-width to the SELECTED BLOCK (text box / paragraph elongation).
  // Pass null to clear. Accepts any CSS length value (e.g. "60%", "800px", "100%").
  function applyMaxWidth(value) {
    const sel = selectedRef.current;
    if (!sel) { toast.message("Click a block first"); return; }
    pushHistory();
    sel.setAttribute("data-uvc-edited", "1");
    if (value == null || value === "") {
      sel.style.removeProperty("max-width");
      sel.style.removeProperty("margin-left");
      sel.style.removeProperty("margin-right");
    } else {
      sel.style.maxWidth = value;
      sel.style.marginLeft = "auto";
      sel.style.marginRight = "auto";
    }
    setDirty(true);
    setTick((t) => t + 1);
  }

  // Adjust the SELECTED BLOCK's vertical spacing (margin-top / margin-bottom).
  // Increments in px so 4 taps == 16px of breathing room. Pass null to clear.
  function bumpMargin(edge /* "top" | "bottom" | "both" */, deltaPx /* number or null */) {
    const sel = selectedRef.current;
    if (!sel) { toast.message("Click a block first"); return; }
    pushHistory();
    sel.setAttribute("data-uvc-edited", "1");
    const edges = edge === "both" ? ["top", "bottom"] : [edge];
    for (const e of edges) {
      const prop = e === "top" ? "marginTop" : "marginBottom";
      if (deltaPx === null) {
        sel.style.removeProperty(e === "top" ? "margin-top" : "margin-bottom");
        continue;
      }
      const cur = parseFloat(sel.style[prop]);
      const base = isFinite(cur) ? cur : 0;
      const next = Math.max(-64, Math.min(128, base + deltaPx));
      sel.style[prop] = `${next}px`;
    }
    setDirty(true);
    setTick((t) => t + 1);
  }

  // Toggle the data-uvc-interactive marker on the selected block. The reader's
  // IntersectionObserver picks this attribute up and shows an Aria activity hint
  // even when the element doesn't look "interactive" to the generic selector
  // (e.g. a styled <div> with a custom onclick).
  function toggleInteractiveMarker() {
    const sel = selectedRef.current;
    if (!sel) { toast.message("Click a block first"); return; }
    pushHistory();
    sel.setAttribute("data-uvc-edited", "1");
    if (sel.hasAttribute("data-uvc-interactive")) {
      sel.removeAttribute("data-uvc-interactive");
      toast.message("Interactive marker removed");
    } else {
      sel.setAttribute("data-uvc-interactive", "1");
      toast.success("Marked as interactive", {
        description: "Save, then open the top-right Preview button (or your Demo link) — Aria will float in and alert learners on this block.",
      });
    }
    setDirty(true);
    setTick((t) => t + 1);
  }

  // Toggle contentEditable on the editable root when interactMode changes,
  // so clicks pass through to the artifact's own buttons/links/iframes.
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    if (interactMode) {
      root.setAttribute("contenteditable", "false");
      root.setAttribute("data-uvc-interact-mode", "1");
      // Hide any selection markers in interact mode
      root.querySelectorAll("[data-uvc-selected],[data-uvc-multi-selected]").forEach((n) => {
        n.removeAttribute("data-uvc-selected");
        n.removeAttribute("data-uvc-multi-selected");
      });
      selectedRef.current = null;
      multiSelectedRef.current = [];
      setFloatingPos(null);
    } else {
      root.setAttribute("contenteditable", "true");
      root.removeAttribute("data-uvc-interact-mode");
    }
  }, [interactMode]);

  const recomputeFloating = React.useCallback(() => {
    const outer = editorWrapRef.current;
    const multi = (multiSelectedRef.current || []).filter((el) => el && el.isConnected);
    const list = multi.length ? multi : (selectedRef.current && selectedRef.current.isConnected ? [selectedRef.current] : []);
    if (!outer || list.length === 0) { setFloatingPos(null); setResizeRect(null); return; }

    const outerR = outer.getBoundingClientRect();
    // In iframe mode the selected element lives inside the sandboxed iframe,
    // so its getBoundingClientRect is iframe-viewport-relative. We must add the
    // iframe's offset within the parent to map back to outer-wrapper coords.
    const iframeR = (isIframeMode && iframeRef.current) ? iframeRef.current.getBoundingClientRect() : null;
    const toLocal = (r) => {
      if (iframeR) {
        return {
          top: iframeR.top + r.top - outerR.top,
          left: iframeR.left + r.left - outerR.left,
          right: iframeR.left + r.right - outerR.left,
          bottom: iframeR.top + r.bottom - outerR.top,
          width: r.width,
          height: r.height,
        };
      }
      return {
        top: r.top - outerR.top,
        left: r.left - outerR.left,
        right: r.right - outerR.left,
        bottom: r.bottom - outerR.top,
        width: r.width,
        height: r.height,
      };
    };

    let topMin = Infinity, rightMax = -Infinity;
    for (const el of list) {
      const r = toLocal(el.getBoundingClientRect());
      topMin = Math.min(topMin, r.top);
      rightMax = Math.max(rightMax, r.right);
    }
    if (!isFinite(topMin)) { setFloatingPos(null); setResizeRect(null); return; }
    setFloatingPos({ top: topMin, left: rightMax + 12 });

    // Resize-handles overlay — only when a *single* element is selected.
    // We show handles around images / iframes / video / etc. and block-level
    // boxes (sections, divs, headings) — not raw text spans.
    const sole = list.length === 1 ? list[0] : null;
    if (!sole) { setResizeRect(null); return; }
    const tag = (sole.tagName || "").toLowerCase();
    const isImageLike = /^(img|iframe|video|audio|embed|object|canvas|svg)$/.test(tag);
    let cs;
    try {
      const win = sole.ownerDocument?.defaultView || window;
      cs = win.getComputedStyle(sole);
    } catch (_) { cs = null; }
    const isBlock = cs && /block|grid|flex/.test(cs.display || "");
    if (!isImageLike && !isBlock) { setResizeRect(null); return; }
    const r = toLocal(sole.getBoundingClientRect());
    setResizeRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
      kind: isImageLike ? "image" : "block",
    });
  }, [isIframeMode]);

  React.useEffect(() => { recomputeFloating(); }, [tick, recomputeFloating]);
  React.useEffect(() => {
    const onAny = () => recomputeFloating();
    const inlineScroll = editorScrollRef.current;
    inlineScroll?.addEventListener("scroll", onAny, { passive: true });
    window.addEventListener("resize", onAny);
    // In iframe mode, also follow the iframe content's own scroll so the
    // overlay tracks the selected element as the admin scrolls the artifact.
    const iframeDoc = isIframeMode ? iframeRef.current?.contentDocument : null;
    const iframeWin = isIframeMode ? iframeRef.current?.contentWindow : null;
    iframeDoc?.addEventListener("scroll", onAny, { passive: true, capture: true });
    iframeWin?.addEventListener("resize", onAny);
    return () => {
      inlineScroll?.removeEventListener("scroll", onAny);
      window.removeEventListener("resize", onAny);
      iframeDoc?.removeEventListener("scroll", onAny, true);
      iframeWin?.removeEventListener("resize", onAny);
    };
  }, [recomputeFloating, isIframeMode, tick]);

  // Marquee (drag-select) — inline mode only; disabled while interactMode is on
  React.useEffect(() => {
    if (!marqueeMode || !isInlineMode || interactMode) return undefined;
    const root = rootRef.current;
    const scroll = editorScrollRef.current;
    if (!root || !scroll) return undefined;
    root.setAttribute("contenteditable", "false");
    scroll.style.cursor = "crosshair";
    let startX = 0, startY = 0, active = false;

    const toLocal = (e) => {
      const r = scroll.getBoundingClientRect();
      return { x: e.clientX - r.left + scroll.scrollLeft, y: e.clientY - r.top + scroll.scrollTop };
    };

    const onDown = (e) => {
      if (e.button !== 0) return;
      const p = toLocal(e);
      startX = p.x; startY = p.y; active = true;
      setMarqueeRect({ left: startX, top: startY, width: 0, height: 0 });
      e.preventDefault();
    };
    const onMove = (e) => {
      if (!active) return;
      const p = toLocal(e);
      setMarqueeRect({
        left: Math.min(startX, p.x),
        top: Math.min(startY, p.y),
        width: Math.abs(p.x - startX),
        height: Math.abs(p.y - startY),
      });
    };
    const onUp = (e) => {
      if (!active) return;
      active = false;
      const p = toLocal(e);
      const rect = {
        left: Math.min(startX, p.x),
        top: Math.min(startY, p.y),
        right: Math.max(startX, p.x),
        bottom: Math.max(startY, p.y),
      };
      // Clear existing
      root.querySelectorAll("[data-uvc-selected],[data-uvc-multi-selected]").forEach((n) => {
        n.removeAttribute("data-uvc-selected");
        n.removeAttribute("data-uvc-multi-selected");
      });
      multiSelectedRef.current = [];
      selectedRef.current = null;
      const dragSize = (rect.right - rect.left) + (rect.bottom - rect.top);
      setMarqueeRect(null);
      if (dragSize < 6) {
        setTick((t) => t + 1);
        return;
      }
      const sr = scroll.getBoundingClientRect();
      const matches = [];
      for (const child of Array.from(root.children)) {
        const cr = child.getBoundingClientRect();
        // Skip invisible / non-laid-out nodes (e.g. <style>, <script>, hidden blocks) so they
        // never end up as the "selected" element with bogus textContent.
        if (cr.width <= 0 || cr.height <= 0) continue;
        const childRect = {
          left: cr.left - sr.left + scroll.scrollLeft,
          top: cr.top - sr.top + scroll.scrollTop,
          right: cr.right - sr.left + scroll.scrollLeft,
          bottom: cr.bottom - sr.top + scroll.scrollTop,
        };
        const overlap = !(childRect.right < rect.left || childRect.left > rect.right || childRect.bottom < rect.top || childRect.top > rect.bottom);
        if (overlap) matches.push(child);
      }
      if (matches.length === 1) {
        matches[0].setAttribute("data-uvc-selected", "1");
        selectedRef.current = matches[0];
      } else if (matches.length > 1) {
        matches.forEach((c) => c.setAttribute("data-uvc-multi-selected", "1"));
        multiSelectedRef.current = matches;
        selectedRef.current = matches[0];
      }
      // Auto-exit marquee mode once a selection has been made so floating arrows appear
      if (matches.length > 0) setMarqueeMode(false);
      setTick((t) => t + 1);
    };

    scroll.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      scroll.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Restore editing
      root.setAttribute("contenteditable", "true");
      scroll.style.cursor = "";
      setMarqueeRect(null);
    };
  }, [marqueeMode, isInlineMode]);

  // Insert a new block at the position right after the selected block (or end of root).
  function insertBlock(kind, payload = {}) {
    const root = rootRef.current;
    if (!root) return;
    const doc = root.ownerDocument || document;
    const sel = selectedRef.current;
    // Anchor on the selected element regardless of depth — so inserting an image
    // while a paragraph inside a Section is selected drops the image INSIDE the
    // Section (next sibling of the paragraph) instead of jumping to root.
    const anchor = sel && sel.isConnected && root.contains(sel) && sel !== root ? sel : null;

    let el = null;
    pushHistory();
    const place = (e) => {
      if (!e) return;
      stampKeepSubtree(e); // user-created content must survive the save filter
      if (anchor) anchor.parentElement.insertBefore(e, anchor.nextElementSibling);
      else root.appendChild(e);
      // select the new block & focus into it for typing
      root.querySelectorAll("[data-uvc-selected]").forEach((n) => n.removeAttribute("data-uvc-selected"));
      e.setAttribute("data-uvc-selected", "1");
      selectedRef.current = e;
      // Scroll into view & focus
      try { e.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* iframe scrollIntoView may throw */ }
      if (e.isContentEditable !== false && !["IMG", "IFRAME", "VIDEO", "HR"].includes(e.tagName)) {
        // Place the caret at end of the new block
        try {
          const range = doc.createRange();
          range.selectNodeContents(e);
          range.collapse(false);
          const ws = doc.defaultView || window;
          const dsel = ws.getSelection();
          dsel.removeAllRanges();
          dsel.addRange(range);
        } catch { /* ignore selection failures */ }
      }
      setDirty(true);
      setTick((t) => t + 1);
    };

    if (kind === "h2") { el = doc.createElement("h2"); el.textContent = "Heading"; place(el); return; }
    if (kind === "h3") { el = doc.createElement("h3"); el.textContent = "Subheading"; place(el); return; }
    if (kind === "p") { el = doc.createElement("p"); el.textContent = "Start typing…"; place(el); return; }
    if (kind === "section") {
      el = doc.createElement("section");
      el.style.padding = "24px";
      el.style.borderRadius = "12px";
      el.style.background = "#f8f8f5";
      el.style.margin = "16px 0";
      const h = doc.createElement("h2");
      h.textContent = "Section title";
      h.style.marginTop = "0";
      el.appendChild(h);
      const p = doc.createElement("p");
      p.textContent = "Click here, then use + Insert to add an image, video, or more text inside this section.";
      el.appendChild(p);
      place(el);
      return;
    }
    if (kind === "ul") {
      el = doc.createElement("ul");
      const li = doc.createElement("li");
      li.textContent = "List item";
      el.appendChild(li);
      place(el);
      return;
    }
    if (kind === "hr") { el = doc.createElement("hr"); place(el); return; }
    if (kind === "image") {
      const wrap = doc.createElement("p");
      wrap.style.textAlign = "center";
      const img = doc.createElement("img");
      img.src = payload.url || "";
      img.alt = payload.alt || "";
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.borderRadius = "8px";
      wrap.appendChild(img);
      place(wrap);
      return;
    }
    if (kind === "video") {
      // For YouTube/Vimeo: iframe; for direct .mp4 etc: <video> tag
      const url = payload.url;
      const yt = toYouTubeEmbed(url);
      const vimeo = toVimeoEmbed(url);
      let videoEl;
      if (yt || vimeo) {
        const wrap = doc.createElement("div");
        wrap.style.position = "relative";
        wrap.style.paddingBottom = "56.25%"; // 16:9
        wrap.style.height = "0";
        wrap.style.overflow = "hidden";
        wrap.style.borderRadius = "8px";
        wrap.style.margin = "1.5rem 0";
        const iframe = doc.createElement("iframe");
        iframe.src = yt || vimeo;
        iframe.title = "Embedded video";
        iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture");
        iframe.setAttribute("allowfullscreen", "true");
        iframe.style.position = "absolute";
        iframe.style.top = "0";
        iframe.style.left = "0";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.border = "0";
        wrap.appendChild(iframe);
        videoEl = wrap;
      } else {
        // Direct video file URL
        const wrap = doc.createElement("div");
        wrap.style.margin = "1.5rem 0";
        const v = doc.createElement("video");
        v.src = url;
        v.controls = true;
        v.style.width = "100%";
        v.style.borderRadius = "8px";
        wrap.appendChild(v);
        videoEl = wrap;
      }
      place(videoEl);
      return;
    }
  }

  function describeSelected() {
    const multi = (multiSelectedRef.current || []).filter((el) => {
      if (!el || !el.isConnected) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
    if (multi.length > 1) {
      return { tag: `${multi.length} blocks`, text: "Move them as a group with ⬆/⬇", multi: true };
    }
    const sel = selectedRef.current;
    if (!sel || !sel.isConnected) return null;
    const r = sel.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    const tag = (sel.tagName || "").toLowerCase();
    const text = (sel.textContent || "").trim().replace(/\s+/g, " ").slice(0, 60);
    // In embed mode, elements without a source stamp were created at runtime
    // by the artifact's own script — edits to them get rebuilt on every load.
    let scriptGenerated = isIframeMode && !sel.hasAttribute("data-uvc-i") && !sel.hasAttribute("data-uvc-keep");
    // Also flag stamped elements whose text the script has ALREADY rewritten
    // at runtime (e.g. live counters / progress labels): edits there get
    // overwritten the next time the module loads.
    if (!scriptGenerated && isIframeMode && sel.hasAttribute("data-uvc-i")) {
      // Flagged by the load-time MutationObserver (self or a direct child)?
      if (sel.hasAttribute("data-uvc-script-touched") ||
          Array.from(sel.children).some((c) => c.hasAttribute("data-uvc-script-touched"))) {
        scriptGenerated = true;
      } else if (!sel.hasAttribute("data-uvc-edited") && !sel.querySelector("[data-uvc-edited],[data-uvc-keep]")) {
        const pris = getPristineText(sel.getAttribute("data-uvc-i"));
        const liveTxt = (sel.textContent || "").trim().replace(/\s+/g, " ");
        if (pris != null && pris !== liveTxt) scriptGenerated = true;
      }
    }
    return { tag, text: text || "(empty block)", multi: false, scriptGenerated };
  }

  // Lazy map of source stamp -> pristine text, used to detect elements whose
  // text is rewritten at runtime by the artifact's own script.
  const pristineTextRef = React.useRef(null);
  function getPristineText(stamp) {
    if (!pristineTextRef.current) {
      const map = new Map();
      try {
        const d = new DOMParser().parseFromString(initialEditableHtml, "text/html");
        d.querySelectorAll("[data-uvc-i]").forEach((el) => {
          map.set(el.getAttribute("data-uvc-i"), (el.textContent || "").trim().replace(/\s+/g, " "));
        });
      } catch { /* malformed source */ }
      pristineTextRef.current = map;
    }
    return pristineTextRef.current.get(stamp);
  }

  async function save() {
    setSaving(true);
    try {
      let newContent;
      // Clear selection markers before serializing
      const root = rootRef.current;
      if (root) {
        root.querySelectorAll("[data-uvc-selected],[data-uvc-multi-selected]").forEach((n) => {
          n.removeAttribute("data-uvc-selected");
          n.removeAttribute("data-uvc-multi-selected");
        });
      }
      if (isIframeMode) {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) throw new Error("Editor not ready");
        // Rebuild a clean source document: drops script-generated runtime
        // nodes (the script recreates them on load) and restores pristine
        // attributes on elements the user never touched, so the saved file
        // renders identically in admin preview and the learner portal.
        newContent = serializeEditedIframe(doc, initialEditableHtml);
      } else {
        const cleanRoot = inlineRef.current.cloneNode(true);
        stripUvcInternalAttrs(cleanRoot);
        newContent = cleanRoot.innerHTML;
        // Restore selection markers so the floating controls stay in sync with the highlighted blocks
        const multi = (multiSelectedRef.current || []).filter((el) => el && el.isConnected);
        if (multi.length > 1) {
          multi.forEach((el) => el.setAttribute("data-uvc-multi-selected", "1"));
        } else if (selectedRef.current && selectedRef.current.isConnected) {
          selectedRef.current.setAttribute("data-uvc-selected", "1");
        }
      }
      const newType = artifactType === "markdown" ? "html" : artifactType;
      await onSave(newContent, newType);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  // Expose flushSave so the parent can persist iframe-mode DOM edits before
  // switching tabs (where the VisualEditor would otherwise unmount and lose
  // those in-iframe changes).
  React.useImperativeHandle(fwdRef, () => ({
    isDirty: () => dirty,
    flushSave: async () => {
      if (!dirty) return true;
      try { await save(); return true; }
      catch { return false; }
    },
  }), [dirty]);

  // Re-evaluate selected block info on each tick (DOM may have changed)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selected = React.useMemo(() => describeSelected(), [tick]);

  if (isUnsupported) {
    return (
      <div className="h-full flex items-center justify-center p-6 text-center bg-slate-50">
        <div className="max-w-md">
          <Bot size={32} className="mx-auto text-slate-300 mb-3"/>
          <h3 className="text-lg font-display font-semibold text-slate-900 mb-2">Visual editing not available</h3>
          <p className="text-sm text-slate-500">
            {artifactType === "jsx"
              ? <>JSX/React artifacts are interactive code — use the <strong>AI chat on the left</strong> to refine them in plain English, or upload a new version.</>
              : <>External links don't have editable content. Update the URL in the panel above to change the destination.</>}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Top toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-white shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {selected ? (
            <div className="flex items-center gap-1 min-w-0">
              <span className="px-1.5 py-0.5 bg-[#E53935]/10 text-[#E53935] rounded text-[10px] uppercase font-mono font-bold shrink-0">
                {selected.tag}
              </span>
              {selected.scriptGenerated && (
                <span
                  className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-semibold shrink-0"
                  title="This element is generated at runtime by the module's own script. It gets rebuilt on every load, so edits made here won't persist — edit the underlying content or use Edit code instead."
                  data-testid="script-generated-warning"
                >
                  auto-generated — edits won't stick
                </span>
              )}
              <span className="text-xs text-slate-600 truncate" data-testid="visual-selected-summary">
                {selected.text}
              </span>
            </div>
          ) : (
            <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
              <MousePointerClick size={12} className="text-[#E53935]"/>
              Click any text to edit · click a block to select it
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Interact mode toggle — works for both inline and iframe modes;
              flips contenteditable so clicks pass through to the artifact's
              own buttons / Next slide / Tabs / etc. */}
          <button
            onClick={() => setInteractMode((m) => !m)}
            className={`h-7 px-2 text-xs rounded border inline-flex items-center gap-1.5 mr-1 transition-colors ${
              interactMode
                ? "bg-blue-900 text-white border-blue-900 shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:border-blue-500 hover:text-blue-700"
            }`}
            data-testid="interact-mode-toggle"
            title="Interact mode — click through to the artifact's buttons & links without editing"
          >
            <Hand size={12}/> {interactMode ? "Interact on" : "Interact"}
          </button>
          {/* Undo / Redo */}
          <div className="flex items-center gap-0.5 mr-1 pr-1 border-r border-slate-200">
            <button
              onClick={undo}
              disabled={historyRef.current.past.length === 0}
              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid="visual-undo"
              title="Undo (⌘/Ctrl+Z)"
            ><Undo2 size={14}/></button>
            <button
              onClick={redo}
              disabled={historyRef.current.future.length === 0}
              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid="visual-redo"
              title="Redo (⌘/Ctrl+Shift+Z)"
            ><Redo2 size={14}/></button>
          </div>
          {/* Marquee drag-select toggle (inline mode only) */}
          {isInlineMode && (
            <button
              onClick={() => setMarqueeMode((m) => !m)}
              className={`h-7 px-2 text-xs rounded border inline-flex items-center gap-1.5 mr-1 transition-colors ${
                marqueeMode
                  ? "bg-[#E53935] text-white border-[#E53935] shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
              data-testid="marquee-mode-toggle"
              title="Drag a rectangle on the canvas to select one or more sections"
            >
              <BoxSelect size={12}/> {marqueeMode ? "Drag-select on" : "Drag-select"}
            </button>
          )}
          {/* + Insert dropdown */}
          <Popover open={insertOpen} onOpenChange={setInsertOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 mr-1"
                data-testid="insert-block-trigger"
              >
                <Plus size={12}/> Insert
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1" data-testid="insert-block-menu">
              <InsertMenuItem icon={BoxSelect} label="Section" hint="Container — add media inside" onClick={() => { insertBlock("section"); setInsertOpen(false); }} testid="insert-section"/>
              <InsertMenuItem icon={Heading2} label="Heading" hint="Section title" onClick={() => { insertBlock("h2"); setInsertOpen(false); }} testid="insert-h2"/>
              <InsertMenuItem icon={Heading3} label="Subheading" hint="Sub-section" onClick={() => { insertBlock("h3"); setInsertOpen(false); }} testid="insert-h3"/>
              <InsertMenuItem icon={AlignLeft} label="Paragraph" hint="Body text" onClick={() => { insertBlock("p"); setInsertOpen(false); }} testid="insert-paragraph"/>
              <InsertMenuItem icon={ListIcon} label="Bulleted list" hint="Bullet points" onClick={() => { insertBlock("ul"); setInsertOpen(false); }} testid="insert-list"/>
              <div className="my-1 h-px bg-slate-200"/>
              <InsertMenuItem icon={VideoIcon} label="Video" hint="YouTube/Vimeo or upload" onClick={() => { setMediaPicker({ kind: "video" }); setInsertOpen(false); }} testid="insert-video"/>
              <InsertMenuItem icon={ImageIcon} label="Image" hint="URL or upload" onClick={() => { setMediaPicker({ kind: "image" }); setInsertOpen(false); }} testid="insert-image"/>
              <div className="my-1 h-px bg-slate-200"/>
              <InsertMenuItem icon={Minus} label="Divider" hint="Horizontal line" onClick={() => { insertBlock("hr"); setInsertOpen(false); }} testid="insert-divider"/>
            </PopoverContent>
          </Popover>

          {/* Font family picker — applies to highlighted text range, or to selected block */}
          <Popover open={fontOpen} onOpenChange={setFontOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 mr-1"
                data-testid="font-picker-trigger"
                title="Change font of selected text or block"
              >
                <Type size={12}/> Font
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1" data-testid="font-picker-menu">
              <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Apply font
              </div>
              {FONT_CHOICES.map((f) => (
                <button
                  key={f.id}
                  onClick={() => { applyFont(f.css); setFontOpen(false); }}
                  className="w-full flex items-center justify-between gap-3 px-2 py-1.5 rounded hover:bg-slate-100 text-left transition-colors text-sm text-slate-800"
                  data-testid={`font-option-${f.id}`}
                >
                  <span style={{ fontFamily: f.css || "inherit" }} className="truncate">
                    {f.label}
                  </span>
                  {f.id === "default" && <span className="text-[10px] text-slate-400 font-mono">reset</span>}
                </button>
              ))}
              <div className="px-2 pt-1 pb-1.5 text-[10px] text-slate-500 border-t border-slate-100 mt-1 leading-snug">
                Highlight text to restyle just that text, or click a block to restyle the whole block.
              </div>
            </PopoverContent>
          </Popover>

          {/* Sections panel — list / rename / reorder / remove artifact sections */}
          <Popover open={sectionsOpen} onOpenChange={setSectionsOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 mr-1"
                data-testid="sections-panel-trigger"
                title="Manage artifact sections: rename, reorder or remove entire sections"
              >
                <ListTree size={12}/> Sections
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0" data-testid="sections-popover">
              {sectionsOpen && (
                <SectionsPanel
                  getRoot={() => (isIframeMode ? iframeRef.current?.contentDocument?.body : inlineRef.current)}
                  onBeforeChange={pushHistory}
                  onAfterChange={() => {
                    // Clear selection if it pointed inside a removed subtree
                    if (selectedRef.current && !selectedRef.current.isConnected) selectedRef.current = null;
                    multiSelectedRef.current = (multiSelectedRef.current || []).filter((el) => el.isConnected);
                    setDirty(true);
                    setTick((t) => t + 1);
                  }}
                />
              )}
            </PopoverContent>
          </Popover>

          {/* Color picker — text & background fills */}
          <Popover open={colorOpen} onOpenChange={setColorOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 mr-1"
                data-testid="color-picker-trigger"
                title="Set text or background color"
              >
                <Palette size={12}/> Color
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3" data-testid="color-picker-menu">
              <ColorPaletteSection
                label="Text color"
                testidPrefix="color-text"
                onPick={(css) => applyColor(css, "text")}
              />
              <div className="my-3 h-px bg-slate-200"/>
              <ColorPaletteSection
                label="Background color"
                testidPrefix="color-bg"
                onPick={(css) => applyColor(css, "bg")}
              />
              <div className="text-[10px] text-slate-500 mt-3 leading-snug border-t border-slate-100 pt-2">
                Highlight text to color just that text. Click a block first to fill its background.
              </div>
            </PopoverContent>
          </Popover>

          {/* Format popover — Word-style: B/I/U, headings, size, alignment, lists, link */}
          <Popover open={formatOpen} onOpenChange={setFormatOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 mr-1"
                data-testid="format-popover-trigger"
                title="Word-style text formatting"
              >
                <Bold size={12}/> Format
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-3" data-testid="format-popover">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Style</div>
              <div className="flex items-center gap-1 mb-3">
                <FormatButton onClick={fmtBold} title="Bold (⌘/Ctrl+B)" testid="fmt-bold"><Bold size={14}/></FormatButton>
                <FormatButton onClick={fmtItalic} title="Italic (⌘/Ctrl+I)" testid="fmt-italic"><Italic size={14}/></FormatButton>
                <FormatButton onClick={fmtUnderline} title="Underline (⌘/Ctrl+U)" testid="fmt-underline"><Underline size={14}/></FormatButton>
                <FormatButton onClick={fmtLink} title="Insert link (⌘/Ctrl+K)" testid="fmt-link"><Link2 size={14}/></FormatButton>
                <div className="w-px h-5 bg-slate-200 mx-1"/>
                <FormatButton onClick={fmtClear} title="Clear formatting" testid="fmt-clear"><RotateCcw size={13}/></FormatButton>
              </div>

              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Heading</div>
              <div className="flex items-center gap-1 mb-3">
                {HEADING_CHOICES.map((h) => (
                  <button
                    key={h.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => fmtHeading(h.tag)}
                    className="flex-1 h-7 text-xs rounded border border-slate-200 bg-white hover:border-[#E53935] hover:text-[#E53935] transition-colors"
                    data-testid={`fmt-heading-${h.id}`}
                    title={h.label}
                  >{h.id === "p" ? "P" : h.tag}</button>
                ))}
              </div>

              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Text size</div>
              <div className="grid grid-cols-6 gap-1 mb-3">
                {TEXT_SIZES.map((s) => (
                  <button
                    key={s.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyTextSize(s.css)}
                    className="h-7 text-[10px] rounded border border-slate-200 bg-white hover:border-[#E53935] hover:text-[#E53935] transition-colors"
                    style={s.css ? { fontSize: `min(${s.css}, 14px)` } : {}}
                    data-testid={`fmt-size-${s.id}`}
                    title={s.label}
                  >{s.label}</button>
                ))}
              </div>

              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Alignment</div>
              <div className="flex items-center gap-1 mb-3">
                <FormatButton onClick={() => fmtAlign("left")} title="Align left" testid="fmt-align-left"><AlignLeft size={14}/></FormatButton>
                <FormatButton onClick={() => fmtAlign("center")} title="Align center" testid="fmt-align-center"><AlignCenter size={14}/></FormatButton>
                <FormatButton onClick={() => fmtAlign("right")} title="Align right" testid="fmt-align-right"><AlignRight size={14}/></FormatButton>
                <FormatButton onClick={() => fmtAlign("justify")} title="Justify" testid="fmt-align-justify"><AlignJustify size={14}/></FormatButton>
                <div className="w-px h-5 bg-slate-200 mx-1"/>
                <FormatButton onClick={() => fmtList("unordered")} title="Bulleted list" testid="fmt-list-ul"><ListIcon size={14}/></FormatButton>
                <FormatButton onClick={() => fmtList("ordered")} title="Numbered list" testid="fmt-list-ol"><ListOrdered size={14}/></FormatButton>
              </div>

              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Block width <span className="text-slate-400 normal-case">(elongate / narrow the selected box)</span>
              </div>
              <div className="grid grid-cols-5 gap-1 mb-3" data-testid="fmt-block-width">
                {[
                  { id: "narrow", label: "60%", val: "60%" },
                  { id: "medium", label: "75%", val: "75%" },
                  { id: "wide", label: "90%", val: "90%" },
                  { id: "full", label: "100%", val: "100%" },
                  { id: "reset", label: "Reset", val: null },
                ].map((w) => (
                  <button
                    key={w.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyMaxWidth(w.val)}
                    className="h-7 text-[10px] rounded border border-slate-200 bg-white hover:border-[#E53935] hover:text-[#E53935] transition-colors"
                    data-testid={`fmt-width-${w.id}`}
                    title={w.val ? `Set max-width to ${w.val}` : "Clear custom width"}
                  >{w.label}</button>
                ))}
              </div>

              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Spacing <span className="text-slate-400 normal-case">(tap to add / remove gap above &amp; below the selected block)</span>
              </div>
              <div className="space-y-1.5 mb-3" data-testid="fmt-spacing">
                {[
                  { edge: "top", label: "Above" },
                  { edge: "bottom", label: "Below" },
                ].map((row) => (
                  <div key={row.edge} className="flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-500 w-12 shrink-0">{row.label}</span>
                    {[-16, -8, -4, 4, 8, 16].map((d) => (
                      <button
                        key={d}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => bumpMargin(row.edge, d)}
                        className="h-7 flex-1 text-[10px] rounded border border-slate-200 bg-white hover:border-[#E53935] hover:text-[#E53935] transition-colors font-mono"
                        data-testid={`fmt-spacing-${row.edge}-${d > 0 ? "plus" : "minus"}${Math.abs(d)}`}
                        title={`${d > 0 ? "+" : ""}${d}px to ${row.label.toLowerCase()}`}
                      >{d > 0 ? `+${d}` : d}</button>
                    ))}
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => bumpMargin(row.edge, null)}
                      className="h-7 px-2 text-[10px] rounded border border-slate-200 bg-slate-50 hover:border-slate-400 transition-colors"
                      data-testid={`fmt-spacing-${row.edge}-reset`}
                      title={`Reset ${row.label.toLowerCase()} margin`}
                    >×</button>
                  </div>
                ))}
              </div>

              <div className="text-[10px] text-slate-500 leading-snug border-t border-slate-100 pt-2">
                Highlight text for inline styling, or click a block to restyle the whole block.
                <br/><span className="text-[#E53935] font-medium">Tip:</span> drag the orange handle on a selected image / text box to resize it freehand.
              </div>
            </PopoverContent>
          </Popover>

          {/* Mark as interactive — flags the selected block so Aria alerts learners */}
          <button
            onClick={toggleInteractiveMarker}
            disabled={!selected}
            className={`h-7 px-2 text-xs rounded border inline-flex items-center gap-1.5 mr-1 transition-colors disabled:opacity-30 ${
              (selectedRef.current?.hasAttribute("data-uvc-interactive"))
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-blue-500 hover:text-blue-700"
            }`}
            data-testid="mark-interactive-toggle"
            title="Tag this block so Aria alerts learners when they scroll near it"
          >
            <Zap size={12}/> Interactive
          </button>

          {/* Block action buttons */}
          <div className="flex items-center gap-0.5 mr-1 pr-1 border-r border-slate-200">
            <button
              onClick={selectParent}
              disabled={!selected}
              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid="block-select-parent"
              title="Select parent element (climb DOM tree)"
            ><ChevronsUp size={14}/></button>
            <button
              onClick={() => performAction("up")}
              disabled={!selected}
              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid="block-move-up"
              title="Move selected block up"
            ><ChevronUp size={14}/></button>
            <button
              onClick={() => performAction("down")}
              disabled={!selected}
              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid="block-move-down"
              title="Move selected block down"
            ><ChevronDown size={14}/></button>
            <button
              onClick={() => performAction("duplicate")}
              disabled={!selected}
              className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid="block-duplicate"
              title="Duplicate selected block"
            ><Copy size={13}/></button>
            <button
              onClick={() => performAction("delete")}
              disabled={!selected}
              className="p-1.5 text-slate-500 hover:text-[#E53935] hover:bg-[#E53935]/10 rounded disabled:opacity-30 disabled:hover:bg-transparent"
              data-testid="block-delete"
              title="Delete selected block"
            ><Trash2 size={13}/></button>
          </div>
          {dirty && (
            <span data-testid="visual-dirty-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-[#E53935]/10 text-[#E53935] font-semibold mr-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E53935] animate-pulse"/> Unsaved
            </span>
          )}
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            className="h-7 bg-[#E53935] hover:bg-[#c62828] text-white text-xs px-3 disabled:opacity-50"
            data-testid="save-visual-button"
          >
            {saving
              ? <><Loader2 size={12} className="animate-spin mr-1"/> Saving</>
              : <><Save size={12} className="mr-1"/> Save</>}
          </Button>
        </div>
      </div>

      {/* Editable surface */}
      <div
        ref={editorWrapRef}
        className="flex-1 min-h-0 overflow-hidden relative"
        data-testid="visual-editor-wrap"
      >
        {isIframeMode ? (
          <iframe
            ref={iframeRef}
            title="visual-editor-iframe"
            srcDoc={editorSrcDoc}
            sandbox="allow-scripts allow-same-origin allow-forms"
            className="w-full h-full border-0 block bg-white"
            data-testid="visual-editor-iframe"
          />
        ) : (
          <div
            ref={editorScrollRef}
            className="h-full overflow-y-auto thin-scroll bg-white relative"
            data-testid="visual-editor-scroll"
          >
            <style>{`
              .uvc-visual-edit [data-uvc-selected] {
                outline: 2px solid #E53935 !important;
                outline-offset: 6px !important;
                border-radius: 4px;
              }
              .uvc-visual-edit [data-uvc-multi-selected] {
                outline: 2px solid #E53935 !important;
                outline-offset: 6px !important;
                border-radius: 4px;
                background-color: rgba(229, 57, 53, 0.06);
              }
              .uvc-visual-edit [data-uvc-interactive] {
                outline: 2px dashed #1d4ed8 !important;
                outline-offset: 6px !important;
                border-radius: 4px;
              }
              .uvc-visual-edit[data-uvc-interact-mode="1"] [data-uvc-selected],
              .uvc-visual-edit[data-uvc-interact-mode="1"] [data-uvc-multi-selected] { outline: none !important; }
              .uvc-visual-edit:focus-within { outline: none; }
            `}</style>
            <div
              ref={setInlineRef}
              className="uvc-visual-edit artifact-prose max-w-3xl mx-auto px-10 py-10 outline-none min-h-[300px]"
              suppressContentEditableWarning
              data-testid="visual-editor-inline"
            />

            {/* Marquee rectangle (drawn while dragging) */}
            {marqueeRect && (
              <div
                className="absolute pointer-events-none border-2 border-[#E53935] bg-[#E53935]/10 rounded"
                style={{
                  left: marqueeRect.left,
                  top: marqueeRect.top,
                  width: marqueeRect.width,
                  height: marqueeRect.height,
                }}
                data-testid="marquee-rect"
              />
            )}
          </div>
        )}

        {/* === Overlays rendered in the outer wrapper so they work in BOTH ===
            === iframe (Claude HTML) and inline (Markdown) editor modes.    === */}

        {/* Drag-to-resize handles — 8 around the selected image / block.
            Shift-drag breaks aspect lock on corners. Corner handles always
            resize; side handles resize one axis. We apply inline width/
            height (image-like) or max-width/min-height (block). pushHistory()
            is called once on drag-start so undo is a single step. */}
        {resizeRect && !marqueeMode && !interactMode && (() => {
          const HANDLES = [
            { id: "nw", x: 0,   y: 0,   cur: "nwse-resize" },
            { id: "n",  x: 0.5, y: 0,   cur: "ns-resize"   },
            { id: "ne", x: 1,   y: 0,   cur: "nesw-resize" },
            { id: "e",  x: 1,   y: 0.5, cur: "ew-resize"   },
            { id: "se", x: 1,   y: 1,   cur: "nwse-resize" },
            { id: "s",  x: 0.5, y: 1,   cur: "ns-resize"   },
            { id: "sw", x: 0,   y: 1,   cur: "nesw-resize" },
            { id: "w",  x: 0,   y: 0.5, cur: "ew-resize"   },
          ];
          const onHandleDown = (handleId) => (e) => {
            e.preventDefault();
            e.stopPropagation();
            const sel = selectedRef.current;
            if (!sel) return;
            pushHistory();
            const startBox = sel.getBoundingClientRect();
            const startW = startBox.width;
            const startH = startBox.height;
            const startX = e.clientX;
            const startY = e.clientY;
            const aspect = startW / Math.max(startH, 1);
            const isImg = resizeRect.kind === "image";
            const isCorner = handleId.length === 2;
            const affectsX = handleId.includes("e") || handleId.includes("w");
            const affectsY = handleId.includes("n") || handleId.includes("s");
            const handleCursor = HANDLES.find((h) => h.id === handleId)?.cur || "default";
            draggingHandleRef.current = handleId;

            // ─── DRAG SHIELD ────────────────────────────────────────────────
            // When the selected element lives inside the sandboxed iframe, the
            // cursor crosses into the iframe during a drag, which swallows
            // mousemove events from the parent window. We mount a transparent
            // <div> over the entire parent window for the duration of the drag
            // so all mouse events stay in the parent document.
            const shield = document.createElement("div");
            shield.setAttribute("data-testid", "resize-drag-shield");
            Object.assign(shield.style, {
              position: "fixed",
              inset: "0",
              zIndex: "999999",
              cursor: handleCursor,
              background: "transparent",
            });
            document.body.appendChild(shield);

            const prevCursor = document.body.style.cursor;
            const prevUserSelect = document.body.style.userSelect;
            document.body.style.cursor = handleCursor;
            document.body.style.userSelect = "none";

            const onMove = (mv) => {
              const dx = mv.clientX - startX;
              const dy = mv.clientY - startY;
              let dw = 0, dh = 0;
              if (handleId.includes("e")) dw = dx;
              if (handleId.includes("w")) dw = -dx;
              if (handleId.includes("s")) dh = dy;
              if (handleId.includes("n")) dh = -dy;
              let newW = Math.max(40, startW + dw);
              let newH = Math.max(20, startH + dh);
              if (isCorner && !mv.shiftKey) {
                const widthDominant = Math.abs(dw) >= Math.abs(dh);
                if (widthDominant) newH = newW / aspect;
                else                newW = newH * aspect;
              }
              if (isImg) {
                if (affectsX || isCorner) {
                  sel.style.setProperty("width", `${Math.round(newW)}px`, "important");
                  sel.style.setProperty("max-width", "none", "important");
                }
                if (affectsY || isCorner) {
                  sel.style.setProperty("height", `${Math.round(newH)}px`, "important");
                }
              } else {
                // Block resize — use `width` + `height` with !important so the
                // overlay's drag actually beats any CSS width:100% / min-height
                // declarations on the element. This is what lets the admin:
                //   • drag E/W middle handles inward to make text wrap to more
                //     lines (true container narrowing)
                //   • drag N/S middle handles up/down to squeeze out empty
                //     padding space between sections (true height shrink, not
                //     just a min-height that only ever grows)
                if (affectsX || isCorner) {
                  sel.style.setProperty("width", `${Math.round(newW)}px`, "important");
                  sel.style.setProperty("max-width", `${Math.round(newW)}px`, "important");
                  sel.style.marginLeft = "auto";
                  sel.style.marginRight = "auto";
                }
                if (affectsY || isCorner) {
                  sel.style.setProperty("height", `${Math.round(newH)}px`, "important");
                  sel.style.setProperty("min-height", "0", "important");
                }
              }
              sel.setAttribute("data-uvc-edited", "1");
              recomputeFloating();
            };
            const onUp = () => {
              shield.removeEventListener("mousemove", onMove);
              shield.removeEventListener("mouseup", onUp);
              document.removeEventListener("mousemove", onMove);
              document.removeEventListener("mouseup", onUp);
              if (shield.parentNode) shield.parentNode.removeChild(shield);
              document.body.style.cursor = prevCursor;
              document.body.style.userSelect = prevUserSelect;
              draggingHandleRef.current = null;
              setDirty(true);
              setTick((t) => t + 1);
            };
            // Listen on both shield and document for redundancy.
            shield.addEventListener("mousemove", onMove);
            shield.addEventListener("mouseup", onUp);
            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
          };
          return (
            <div
              className="absolute z-30 pointer-events-none"
              style={{
                left: resizeRect.left,
                top: resizeRect.top,
                width: resizeRect.width,
                height: resizeRect.height,
              }}
              data-testid="resize-overlay"
            >
              {/* Outline */}
              <div className="absolute inset-0 border-2 border-[#E53935]/70 rounded-sm" />
              {HANDLES.map((h) => (
                <div
                  key={h.id}
                  role="button"
                  aria-label={`Resize handle ${h.id}`}
                  data-testid={`resize-handle-${h.id}`}
                  onMouseDown={onHandleDown(h.id)}
                  className="absolute pointer-events-auto w-3 h-3 -ml-1.5 -mt-1.5 rounded-sm bg-white border-2 border-[#E53935] shadow"
                  style={{
                    left: `${h.x * 100}%`,
                    top: `${h.y * 100}%`,
                    cursor: h.cur,
                  }}
                />
              ))}
            </div>
          );
        })()}

        {/* Floating up/down/delete controls — appear next to selected section(s) */}
        {floatingPos && !marqueeMode && (
          <div
            className="absolute z-30 flex flex-col gap-1 bg-white border border-slate-200 rounded-lg shadow-xl p-1 select-none"
            style={{ top: Math.max(floatingPos.top - 4, 4), left: floatingPos.left }}
            onMouseDown={(e) => e.preventDefault()}
            data-testid="floating-block-controls"
          >
            <button
              onClick={() => performAction("up")}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-700 hover:bg-[#E53935]/10 hover:text-[#E53935] transition-colors"
              title="Move selection up"
              data-testid="floating-move-up"
            >
              <ChevronUp size={18}/>
            </button>
            <button
              onClick={() => performAction("down")}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-700 hover:bg-[#E53935]/10 hover:text-[#E53935] transition-colors"
              title="Move selection down"
              data-testid="floating-move-down"
            >
              <ChevronDown size={18}/>
            </button>
            {/* Replace image — only when an <img> is selected */}
            {selectedRef.current?.tagName === "IMG" && (
              <>
                <div className="h-px bg-slate-200 mx-1"/>
                <button
                  onClick={triggerReplaceImage}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-700 hover:bg-[#E53935]/10 hover:text-[#E53935] transition-colors"
                  title="Replace image (upload a new file)"
                  data-testid="floating-replace-image"
                >
                  <RefreshCw size={14}/>
                </button>
              </>
            )}
            <div className="h-px bg-slate-200 mx-1"/>
            <button
              onClick={() => performAction("delete")}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md text-slate-700 hover:bg-[#E53935] hover:text-white transition-colors"
              title="Delete selection"
              data-testid="floating-delete"
            >
              <Trash2 size={15}/>
            </button>
          </div>
        )}

        {/* Hidden file input for the floating Replace-image button */}
        <input
          ref={replaceImageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="floating-replace-image-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleReplaceImageFile(f);
          }}
        />

        {/* Empty-state starter picker — appears when the artifact has no real content */}
        {!isIframeMode && (initialEditableHtml || "").replace(/<[^>]+>/g, "").trim().length === 0 && !dirty && (
          <div className="absolute inset-x-0 top-16 flex justify-center pointer-events-none">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 max-w-md w-full mx-6 pointer-events-auto" data-testid="visual-empty-picker">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Get started</div>
              <h3 className="text-lg font-display font-bold text-slate-900 mb-1">What goes in this module?</h3>
              <p className="text-xs text-slate-500 mb-4">Pick a starting block — you can add more later from <strong>+ Insert</strong>.</p>
              <div className="grid grid-cols-2 gap-2">
                <StarterChoice icon={Heading2} label="Heading" onClick={() => insertBlock("h2")} testid="starter-heading"/>
                <StarterChoice icon={AlignLeft} label="Paragraph" onClick={() => insertBlock("p")} testid="starter-paragraph"/>
                <StarterChoice icon={VideoIcon} label="Video" onClick={() => setMediaPicker({ kind: "video" })} testid="starter-video"/>
                <StarterChoice icon={ImageIcon} label="Image" onClick={() => setMediaPicker({ kind: "image" })} testid="starter-image"/>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Helper hint */}
      <div className="px-3 py-1.5 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 flex items-center justify-between shrink-0">
        <span className="inline-flex items-center gap-1.5">
          <Wand2 size={10}/> Click element · <strong>Interact</strong> for click-through · <strong>Format</strong> for B/I/U · <strong>⌫</strong> delete · <strong>⌘/Ctrl+Z</strong> undo
        </span>
        <span>⌘/Ctrl+S to save</span>
      </div>

      {/* Media picker — paste URL or upload file (used for both image & video) */}
      <MediaPickerDialog
        kind={mediaPicker?.kind}
        open={!!mediaPicker}
        onClose={() => setMediaPicker(null)}
        onPick={(payload) => {
          insertBlock(mediaPicker.kind, payload);
          setMediaPicker(null);
        }}
      />
    </div>
  );
});

// Small menu row used inside the "+ Insert" popover
function InsertMenuItem({ icon: Icon, label, hint, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-2 py-1.5 rounded hover:bg-slate-100 text-left transition-colors"
      data-testid={testid}
    >
      <span className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center text-slate-700 shrink-0">
        <Icon size={14}/>
      </span>
      <span className="min-w-0">
        <div className="text-xs font-medium text-slate-900 leading-tight">{label}</div>
        <div className="text-[10px] text-slate-500 leading-tight">{hint}</div>
      </span>
    </button>
  );
}

// Big tile used in the empty-state starter card
function StarterChoice({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1.5 py-3 px-2 border border-slate-200 rounded-lg hover:border-[#E53935] hover:bg-[#E53935]/5 transition-colors group"
      data-testid={testid}
    >
      <Icon size={20} className="text-slate-600 group-hover:text-[#E53935] transition-colors"/>
      <span className="text-xs font-medium text-slate-700 group-hover:text-[#E53935]">{label}</span>
    </button>
  );
}

// Small inline icon button for the Format popover
function FormatButton({ onClick, title, testid, children }) {
  return (
    <button
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="w-8 h-7 inline-flex items-center justify-center rounded border border-slate-200 bg-white text-slate-700 hover:border-[#E53935] hover:text-[#E53935] transition-colors"
    >
      {children}
    </button>
  );
}

// Small reusable section that shows a curated palette + a custom hex input
function ColorPaletteSection({ label, testidPrefix, onPick }) {
  const [hex, setHex] = React.useState("");
  return (
    <div data-testid={`${testidPrefix}-section`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center justify-between">
        <span>{label}</span>
      </div>
      <div className="grid grid-cols-6 gap-1.5">
        {COLOR_PALETTE.map((c) => (
          <button
            key={c.id}
            onClick={() => onPick(c.css)}
            className={`w-8 h-8 rounded border ${c.css ? "border-slate-200" : "border-dashed border-slate-300"} hover:scale-110 transition-transform`}
            style={c.css ? { background: c.css } : { background: "repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 4px,#fff 4px,#fff 8px)" }}
            title={c.label}
            data-testid={`${testidPrefix}-${c.id}`}
          />
        ))}
      </div>
      <div className="flex items-center gap-1 mt-2">
        <input
          type="text"
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          placeholder="#hex"
          className="flex-1 min-w-0 px-2 py-1 text-xs font-mono bg-slate-50 border border-slate-200 rounded"
          data-testid={`${testidPrefix}-hex-input`}
        />
        <button
          onClick={() => { if (/^#?[0-9a-fA-F]{3,8}$/.test(hex.trim())) onPick(hex.trim().startsWith("#") ? hex.trim() : `#${hex.trim()}`); }}
          className="px-2 py-1 text-xs bg-[#222222] hover:bg-black text-white rounded"
          data-testid={`${testidPrefix}-hex-apply`}
        >Apply</button>
      </div>
    </div>
  );
}

// Modal that lets admin paste a URL or upload a local file (image / video / audio)
function MediaPickerDialog({ kind, open, onClose, onPick }) {
  const [tab, setTab] = React.useState("url");
  const [url, setUrl] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  React.useEffect(() => {
    if (open) { setTab("url"); setUrl(""); }
  }, [open]);

  if (!open || !kind) return null;
  const title = kind === "video" ? "Insert video" : "Insert image";
  const accept = kind === "video" ? "video/*" : "image/*";
  const urlPlaceholder = kind === "video"
    ? "https://youtu.be/dQw4w9WgXcQ  or  https://vimeo.com/123  or direct .mp4"
    : "https://example.com/image.jpg";

  const submitUrl = () => {
    const clean = (url || "").trim();
    if (!clean) return;
    onPick({ url: normalizeArtifactUrl(clean) });
  };

  const submitUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload-media", form, { headers: { "Content-Type": "multipart/form-data" } });
      // Store as relative URL — iframe renderer + sanitizer absolutize against
      // the current env's backend, so the artifact stays portable across envs.
      onPick({ url: data.url, alt: file.name });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md p-0" data-testid="media-picker-dialog">
        <DialogHeader className="px-5 pt-4 pb-2">
          <DialogTitle className="text-sm font-display flex items-center gap-2">
            {kind === "video" ? <VideoIcon size={14} className="text-[#E53935]"/> : <ImageIcon size={14} className="text-[#E53935]"/>}
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 pb-5">
          <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-md mb-3 text-xs">
            <button
              onClick={() => setTab("url")}
              className={`py-1.5 rounded transition-colors ${tab === "url" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}
              data-testid="media-tab-url"
            >Paste URL</button>
            <button
              onClick={() => setTab("upload")}
              className={`py-1.5 rounded transition-colors ${tab === "upload" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}
              data-testid="media-tab-upload"
            >Upload file</button>
          </div>
          {tab === "url" ? (
            <div className="space-y-3">
              <Input
                autoFocus
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && url.trim()) submitUrl(); }}
                placeholder={urlPlaceholder}
                className="text-sm font-mono"
                data-testid="media-url-input"
              />
              <Button
                onClick={submitUrl}
                disabled={!url.trim()}
                className="w-full bg-[#E53935] hover:bg-[#c62828] text-white"
                data-testid="media-url-submit"
              >Insert</Button>
              {kind === "video" && (
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  YouTube & Vimeo URLs are auto-converted to player embeds. Direct .mp4 / .webm links also work.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <input
                ref={fileRef}
                type="file"
                accept={accept}
                hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) submitUpload(f); }}
                data-testid="media-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full border-2 border-dashed border-slate-300 hover:border-[#E53935] rounded-lg py-8 text-center transition-colors group disabled:opacity-60"
                data-testid="media-upload-trigger"
              >
                {uploading ? (
                  <><Loader2 size={20} className="mx-auto animate-spin text-[#E53935] mb-2"/><div className="text-xs text-slate-600">Uploading…</div></>
                ) : (
                  <><Upload size={20} className="mx-auto text-slate-400 group-hover:text-[#E53935] mb-2 transition-colors"/>
                    <div className="text-xs font-medium text-slate-700">Click to choose {kind === "video" ? "a video file" : "an image"}</div>
                    <div className="text-[10px] text-slate-500 mt-1">{kind === "video" ? "mp4 / webm / mov — up to 200 MB" : "png / jpg / webp / svg"}</div>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// =====================================================================
// Snapshot history — every artifact edit auto-creates a snapshot. Admin can
// browse the last 50 versions for a module and one-click restore to any of them.
// =====================================================================
function SnapshotHistoryButton({ moduleId, onRestored }) {
  const [open, setOpen] = React.useState(false);
  const [snaps, setSnaps] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [restoring, setRestoring] = React.useState(null);
  const { confirm: askConfirm, ConfirmDialog } = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/backup/module/${moduleId}/snapshots`);
      setSnaps(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, moduleId]);

  const restore = async (snap) => {
    const ok = await askConfirm({
      title: "Restore this version?",
      description: `Module content will be replaced with the snapshot from ${formatTaken(snap.taken_at)}. The current version is automatically snapshotted first, so this is safe to undo.`,
      confirmLabel: "Restore",
      destructive: false,
    });
    if (!ok) return;
    setRestoring(snap.id);
    try {
      await api.post(`/backup/module/${moduleId}/restore/${snap.id}`);
      toast.success("Module restored", { description: `Rolled back to ${formatTaken(snap.taken_at)}` });
      await onRestored?.();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-8 text-xs gap-1.5"
        data-testid="snapshot-history-button"
        title="View saved versions of this module"
      >
        <History size={13}/> History
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0" data-testid="snapshot-history-dialog">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-200">
            <DialogTitle className="text-sm font-display flex items-center gap-2">
              <Clock size={13} className="text-[#E53935]"/> Module version history
            </DialogTitle>
            <p className="text-xs text-slate-500 leading-snug">
              Auto-saved on every edit. Keeping the last 50 versions per module.
            </p>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto thin-scroll">
            {loading ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                <Loader2 size={18} className="animate-spin mx-auto mb-2"/> Loading…
              </div>
            ) : snaps.length === 0 ? (
              <div className="py-12 text-center px-6">
                <Clock size={26} className="mx-auto text-slate-300 mb-2"/>
                <div className="text-sm text-slate-600 font-medium">No saved versions yet</div>
                <div className="text-xs text-slate-500 mt-1">As soon as you save an edit, a snapshot appears here.</div>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {snaps.map((s, i) => (
                  <li key={s.id} className="flex items-start gap-3 px-5 py-3 hover:bg-slate-50 transition-colors group">
                    <div className="w-7 h-7 rounded-full bg-slate-100 group-hover:bg-[#E53935]/10 text-slate-600 group-hover:text-[#E53935] flex items-center justify-center shrink-0 transition-colors">
                      <span className="text-[10px] font-mono font-bold">{snaps.length - i}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 font-medium">{formatTaken(s.taken_at)}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                        <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[9px] uppercase font-mono">{s.trigger || "edit"}</span>
                        {s.actor && <span className="truncate">by {s.actor}</span>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restore(s)}
                      disabled={restoring === s.id}
                      className="h-7 text-[11px] gap-1 shrink-0 hover:border-[#E53935] hover:text-[#E53935]"
                      data-testid={`restore-snapshot-${i}`}
                    >
                      {restoring === s.id ? <Loader2 size={11} className="animate-spin"/> : <RotateCcw size={11}/>}
                      Restore
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </>
  );
}

function formatTaken(iso) {
  if (!iso) return "Unknown time";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000; // seconds
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
